import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  Users, Plus, ArrowLeft, ArrowRight, Check, CheckCircle2, XCircle, Clock, RefreshCw,
  Sparkles, SlidersHorizontal,
  Plug, Globe, KeyRound, Trash2, Save, ChevronRight, AlertCircle, Building2, Boxes,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { getUrlParam, setUrlParams } from '../lib/urlState';
import BrandProfileCard from './BrandProfileCard';
import { loadWithCache, invalidate } from '../lib/cache';
import { cn, formatDateTime } from '../lib/utils';
import PageHeader from './ui/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, CardFooter } from './ui/Card';
import { Input, Label, FieldHint, FieldError } from './ui/Input';
import Button from './ui/Button';
import Badge from './ui/Badge';
import EmptyState from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';

/**
 * Zakładka KLIENCI (plan v6 E0 / T05, czat 2, 2026-10-14).
 *
 * Jeden klient = jeden katalog + lista kanałów. Dane: tabele `tenants`, `connections`, `channels`,
 * `price_rules` (migracja 20261014000003, kontrakt K1). Dostęp: tylko admin (RLS `is_admin()`).
 *
 * Sekrety (tokeny, klucze API) NIGDY nie trafiają do tabel ani nie wracają do przeglądarki:
 * formularz wysyła je wyłącznie do edge `connections-test`, która zapisuje je w Supabase Vault
 * i od razu testuje połączenie. Frontend widzi tylko status (ok / błąd), datę testu i metadane
 * (magazyny BASE, storefronty Kauflanda, kanały integratora).
 *
 * Kreator (3 kroki, słowa Maćka w planie v6 §1):
 *   1. Klient: nazwa + jak działamy (integrator | bezpośrednio na platformie)
 *   2. Połączenia: wklej klucze → „Testuj połączenie" → kanały (platforma × rynek) do zaznaczenia
 *   3. Reguły: reguła ceny, numer WEEE, notatki — można pominąć
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const CONNECTIONS_TEST_URL = `${SUPABASE_URL}/functions/v1/connections-test`;
const MANUFACTURERS_SYNC_URL = `${SUPABASE_URL}/functions/v1/manufacturers-sync`;
const MANUFACTURERS_SUGGEST_URL = `${SUPABASE_URL}/functions/v1/manufacturers-suggest`;
const CLIENT_USERS_URL = `${SUPABASE_URL}/functions/v1/client-users`;
const KAUFLAND_CATEGORIES_URL = `${SUPABASE_URL}/functions/v1/kaufland-categories`;
const ALLEGRO_AUTH_URL = `${SUPABASE_URL}/functions/v1/allegro-auth`;

// ─── Słowniki ───────────────────────────────────────────────────────────────

const SYSTEMS = {
  baselinker: { label: 'BaseLinker', kind: 'integrator', color: '#1C64F2', initial: 'B', gives: 'Katalog, ceny i stany. Treść wraca do BASE, kanały wystawia BASE.',
    fields: [{ key: 'token', label: 'Token API', hint: 'BaseLinker → Moje konto → API → Token.', secret: true }] },
  sellasist:  { label: 'Sellasist', kind: 'integrator', color: '#00A86B', initial: 'S', gives: 'Katalog PL, kategorie i cechy. Treść DE idzie na platformę.',
    fields: [
      { key: 'url', label: 'Adres sklepu', placeholder: 'https://nazwa.sellasist.pl', hint: 'Bez ukośnika na końcu.' },
      { key: 'token', label: 'Klucz API', hint: 'Sellasist → Ustawienia → API → klucz (nagłówek apiKey).', secret: true },
    ] },
  kaufland:   { label: 'Kaufland', kind: 'direct', color: '#E10915', initial: 'K', gives: 'Publikacja i synchronizacja ofert na koncie klienta (DE/AT/PL/CZ/SK/FR/IT).',
    fields: [
      { key: 'client_key', label: 'Client Key', hint: 'Seller Portal → Ustawienia → Konto → Seller API.' },
      { key: 'secret_key', label: 'Secret Key', secret: true },
    ] },
  // INT-ALLEGRO (22.10): bez kluczy klienta — klient loguje się na swoje konto Allegro i wpisuje kod (device flow, edge allegro-auth).
  allegro:    { label: 'Allegro', kind: 'direct', color: '#FF5A00', initial: 'a', gives: 'Szkice ofert z produktu po EAN na koncie Allegro klienta (logowanie kodem, bez kluczy).',
    fields: [] },
  // INT-AMAZON (23.10): token sprzedawcy z autoryzacji aplikacji MAMS (SP-API). Test = odczyt rynków konta. Na razie tylko sucha próba.
  amazon:     { label: 'Amazon', kind: 'direct', color: '#FF9900', initial: 'A', gives: 'Sucha próba ofert na Amazon (walidacja Amazona bez zapisu). Wystawianie — wkrótce.',
    fields: [
      { key: 'seller_id', label: 'Seller ID (Merchant Token)', hint: 'Seller Central → Ustawienia → Informacje o koncie → Identyfikator sprzedawcy.' },
      { key: 'refresh_token', label: 'Refresh Token', hint: 'Z autoryzacji aplikacji MAMS w Seller Central (Aplikacje i usługi → Zarządzaj aplikacjami). Zaczyna się od „Atzr|”.', secret: true },
    ] },
};

const MARKETS = [
  { id: 'de', label: 'Niemcy', language: 'de' },
  { id: 'at', label: 'Austria', language: 'de' },
  { id: 'cz', label: 'Czechy', language: 'cs' },
  { id: 'sk', label: 'Słowacja', language: 'sk' },
  { id: 'pl', label: 'Polska', language: 'pl' },
  // v9/B2: storefronty Kauflanda FR i IT
  { id: 'fr', label: 'Francja', language: 'fr' },
  { id: 'it', label: 'Włochy', language: 'it' },
];
// v9/A5: rynki spoza listy Kauflanda direct (kanały z integracji BASE: Kaufland FR/IT, Amazon ES…).
const EXTRA_MARKET_LANGUAGE = { fr: 'fr', it: 'it', es: 'es', nl: 'nl', ro: 'ro', hu: 'hu', en: 'en', gb: 'en', uk: 'en' };
const marketLanguage = (m) => MARKETS.find(x => x.id === m)?.language ?? EXTRA_MARKET_LANGUAGE[m] ?? 'pl';

/** Platformy, które dany system otwiera jako kanały (platforma × rynek). */
function channelsForSystem(system, meta) {
  if (system === 'kaufland') {
    const sf = Array.isArray(meta?.storefronts) ? meta.storefronts : [];
    return MARKETS.filter(m => sf.length === 0 || sf.includes(m.id)).map(m => ({ platform: 'kaufland', market: m.id, label: `Kaufland · ${m.label}` }));
  }
  if (system === 'allegro') return [{ platform: 'allegro', market: 'pl', label: 'Allegro · Polska' }];
  if (system === 'amazon') return [{ platform: 'amazon', market: 'de', label: 'Amazon · Niemcy' }];
  // v9/A5: kanały integratorów liczy ChannelsEditor (integratorChannels) — BASE z getInventoryIntegrations, Sellasist = Allegro.
  if (system === 'sellasist') return [{ platform: 'sellasist_channel', market: 'pl', label: 'Sellasist · sklep PL' }];
  return [];
}

// Platformy, dla których MAMS generuje treść (reszta integracji BASE widoczna, ale jeszcze nieobsługiwana).
const SUPPORTED_CHANNEL_PLATFORMS = new Set(['kaufland', 'allegro', 'amazon']);
const PLATFORM_LABEL = { kaufland: 'Kaufland', allegro: 'Allegro', amazon: 'Amazon', ebay: 'eBay', emag: 'eMAG', temu: 'Temu', erli: 'Erli', empik: 'Empik', shop: 'Sklep' };
const MARKET_NAME = { de: 'Niemcy', at: 'Austria', cz: 'Czechy', sk: 'Słowacja', pl: 'Polska', fr: 'Francja', it: 'Włochy', es: 'Hiszpania', nl: 'Holandia', ro: 'Rumunia', hu: 'Węgry', en: 'UK' };

/**
 * v9/A5: kanały „przez integratora”.
 *  - BASE: lista z `connections.meta.base_integrations` (edge connections-test → getInventoryIntegrations):
 *    kaufland = Kaufland DE, kauflandpl / kauflandfr… = Kaufland PL / FR…, allegro, amazon, ebay… — tylko to, co klient
 *    naprawdę ma podpięte w BASE. Starsze połączenie (bez listy) → { stale: true } i prośba o ponowny test.
 *  - Sellasist: Allegro PL.
 */
function integratorChannels(conn) {
  if (conn.system === 'sellasist') {
    return { items: [{ platform: 'allegro', market: 'pl', label: 'Allegro · Polska (przez Sellasist)', connection_id: conn.id, system: conn.system, via_integrator: true, supported: true }] };
  }
  if (conn.system !== 'baselinker') return { items: [] };
  const list = conn.meta?.base_integrations;
  if (!Array.isArray(list)) return { items: [], stale: true };
  const seen = new Set();
  const items = [];
  list.forEach(i => {
    const key = `${i.platform}:${i.market}`;
    if (seen.has(key)) return;
    seen.add(key);
    const accounts = (i.accounts || []).map(a => a.name).filter(Boolean);
    items.push({
      platform: i.platform, market: i.market, connection_id: conn.id, system: conn.system, via_integrator: true,
      supported: SUPPORTED_CHANNEL_PLATFORMS.has(i.platform),
      base_code: i.code,
      label: `${PLATFORM_LABEL[i.platform] || i.code} · ${MARKET_NAME[i.market] || String(i.market).toUpperCase()} (przez BASE)`,
      accounts,
    });
  });
  return { items };
}


// ─── Edge: test połączenia (jedyna droga sekretu) ────────────────────────────

async function callConnectionsTest(payload) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Brak sesji — zaloguj się ponownie.');
  const res = await fetch(CONNECTIONS_TEST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

// ─── Drobne komponenty ───────────────────────────────────────────────────────

function SystemMark({ system, size = 'md' }) {
  const s = SYSTEMS[system] || { color: '#999', initial: '?' };
  const dim = size === 'sm' ? 'w-6 h-6 text-xs' : 'w-9 h-9 text-sm';
  return (
    <span className={cn('inline-flex items-center justify-center rounded-md font-extrabold text-white flex-shrink-0', dim)} style={{ background: s.color }}>
      {s.initial}
    </span>
  );
}

function ConnectionStatus({ status, testedAt }) {
  if (status === 'ok') return <Badge variant="success" size="sm"><CheckCircle2 className="w-3 h-3" /> działa{testedAt ? ` · ${formatDateTime(testedAt)}` : ''}</Badge>;
  if (status === 'error') return <Badge variant="danger" size="sm"><XCircle className="w-3 h-3" /> błąd</Badge>;
  return <Badge variant="neutral" size="sm"><Clock className="w-3 h-3" /> nietestowane</Badge>;
}

function ModeBadge({ mode }) {
  return mode === 'integrator'
    ? <Badge variant="info" size="sm"><Boxes className="w-3 h-3" /> integrator</Badge>
    : <Badge variant="brand" size="sm"><Globe className="w-3 h-3" /> bezpośrednio</Badge>;
}

/** Formularz kluczy dla jednego systemu; sekret trzymany tylko w stanie komponentu do czasu wysyłki. */
function SecretFields({ system, values, onChange, disabled }) {
  const def = SYSTEMS[system];
  if (!def) return null;
  return (
    <form autoComplete="off" onSubmit={e => e.preventDefault()} className="grid gap-4 sm:grid-cols-2">
      <input type="text" name="fake-user" autoComplete="username" className="hidden" tabIndex={-1} aria-hidden="true" />
      <input type="password" name="fake-pass" autoComplete="current-password" className="hidden" tabIndex={-1} aria-hidden="true" />
      {def.fields.map(f => (
        <div key={f.key} className={cn(f.type === 'checkbox' && 'flex items-center gap-2 sm:col-span-2')}>
          {f.type === 'checkbox' ? (
            <label className="inline-flex items-center gap-2 text-sm text-ink-700 dark:text-ink-300 cursor-pointer">
              <input type="checkbox" disabled={disabled} checked={!!values[f.key]} onChange={e => onChange({ ...values, [f.key]: e.target.checked })} className="w-4 h-4 accent-brand-600" />
              {f.label}
            </label>
          ) : (
            <>
              <Label required>{f.label}</Label>
              <Input
                type={f.secret ? 'password' : 'text'}
                autoComplete={f.secret ? 'new-password' : 'off'}
                name={`mams-${system}-${f.key}-${Math.random().toString(36).slice(2, 6)}`}
                data-lpignore="true"
                data-1p-ignore="true"
                readOnly={!!values.__locked}
                onFocus={() => { if (values.__locked) onChange({ ...values, __locked: false }); }}
                disabled={disabled}
                placeholder={f.placeholder || (f.secret ? '••••••••' : '')}
                value={values[f.key] ?? ''}
                onChange={e => onChange({ ...values, [f.key]: e.target.value })}
              />
              <FieldHint>{f.hint}</FieldHint>
            </>
          )}
        </div>
      ))}
    </form>
  );
}

/** Metadane z testu — czytelnie, bez JSON-a. */
function ConnectionMeta({ system, meta }) {
  if (!meta || Object.keys(meta).length === 0) return null;
  const rows = [];
  if (system === 'baselinker') {
    (meta.inventories || []).forEach(i => rows.push(['Katalog', `${i.name} (#${i.inventory_id}) · języki: ${(i.languages || []).join(', ') || '—'} · magazyny: ${(i.warehouses || []).join(', ') || '—'}`]));
    rows.push(['Kanały w BASE', (meta.integrator_channels || []).length ? meta.integrator_channels.map(c => c.name || c.id).join(', ') : 'brak podpiętych integracji']);
  }
  if (system === 'kaufland') rows.push(['Storefronty', (meta.storefronts || []).map(s => s.toUpperCase()).join(' · ') || '—']);
  if (system === 'sellasist') rows.push(['Sklep', `${meta.shop_url} · kategorie: ${meta.categories_count ?? '—'}`]);
  if (system === 'allegro') {
    const env = meta.env || (meta.sandbox ? 'sandbox' : 'prod');
    rows.push(['Środowisko', env === 'prod' ? `produkcja${meta.read_only !== false ? ' · tylko odczyt' : ''}` : 'sandbox (test)']);
    if (meta.login) rows.push(['Konto Allegro', `${meta.login}${meta.seller_id ? ` (id ${meta.seller_id})` : ''}`]);
    if (meta.authorized_at) rows.push(['Połączono', formatDateTime(meta.authorized_at)]);
  }
  if (system === 'amazon') {
    // Audyt 24.10: meta z testu połączenia (connections-test: marketplaceParticipations).
    rows.push(['Sklep', `${meta.store_name || '—'}${meta.seller_id ? ` (Seller ID ${meta.seller_id})` : ''}`]);
    rows.push(['Rynki', (meta.marketplaces || []).map(m => String(m).toUpperCase()).join(' · ') || '—']);
  }
  if (!rows.length) return null;
  return (
    <dl className="mt-3 grid gap-1 text-xs">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex gap-2">
          <dt className="w-28 flex-shrink-0 font-semibold text-ink-500 dark:text-ink-400">{k}</dt>
          <dd className="text-ink-800 dark:text-ink-200 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Kafelki systemów do dodania połączenia. `preferred` = systemy pasujące do trybu klienta (na górze). */
function SystemPicker({ existing, preferred, onPick }) {
  const entries = Object.entries(SYSTEMS).sort(([a], [b]) => (SYSTEMS[a].kind === preferred ? 0 : 1) - (SYSTEMS[b].kind === preferred ? 0 : 1));
  // Tor 1 → tor 2 (18.10 22:05): klient „przez integratora” — bezpośrednie połączenia z marketplace schowane pod „zaawansowane”,
  // żeby nikt nie kliknął kafelka Allegro zamiast kanału i nie myślał, że trzeba podać klucze Allegro.
  if (preferred === 'integrator') {
    const main = entries.filter(([, d]) => d.kind === 'integrator');
    const adv = entries.filter(([, d]) => d.kind !== 'integrator');
    return (
      <div className="grid gap-3">
        <SystemTiles entries={main} existing={existing} onPick={onPick} />
        {adv.length > 0 && (
          <details className="rounded-lg border border-ink-200 dark:border-ink-700 px-3 py-2">
            <summary className="cursor-pointer text-sm text-ink-600 dark:text-ink-300">Połączenie bezpośrednie z marketplace (zaawansowane) → Dodaj</summary>
            <p className="text-xs text-ink-500 dark:text-ink-400 mt-2 mb-3">Potrzebne tylko, gdy MAMS ma publikować na koncie marketplace klienta z pominięciem integratora (np. Kaufland + powiązanie oferty w Sellasist). Kanały integratora (np. „Allegro · Polska (przez Sellasist)”) wybierasz niżej w „Kanały” — bez kluczy marketplace.</p>
            <SystemTiles entries={adv} existing={existing} onPick={onPick} />
          </details>
        )}
      </div>
    );
  }
  return <SystemTiles entries={entries} existing={existing} onPick={onPick} />;
}

function SystemTiles({ entries, existing, onPick }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map(([id, sdef]) => {
        const has = existing.includes(id);
        // Jeden klient = jeden integrator: przy BaseLinkerze nie da się dodać Sellasista (i odwrotnie).
        const otherIntegrator = sdef.kind === 'integrator' ? existing.find(x => x !== id && SYSTEMS[x]?.kind === 'integrator') : null;
        const off = sdef.disabled || has || !!otherIntegrator;
        return (
          <button
            key={id}
            type="button"
            disabled={off}
            onClick={() => onPick(id)}
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
              off ? 'border-ink-100 dark:border-ink-800 opacity-60 cursor-not-allowed' : 'border-ink-200 hover:border-brand-400 hover:bg-brand-50/40 dark:border-ink-700 dark:hover:border-brand-600 dark:hover:bg-brand-900/20',
            )}
          >
            <SystemMark system={id} />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink-900 dark:text-ink-50">{sdef.label}{has && <span className="ml-2 text-2xs font-medium text-ink-400">dodane</span>}</span>
              <span className="block text-xs text-ink-500 dark:text-ink-400 leading-snug mt-0.5">{otherIntegrator ? `Klient ma już integratora ${SYSTEMS[otherIntegrator].label} — jeden klient = jeden integrator.` : sdef.gives}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

const MODES = [
  { id: 'integrator', title: 'Przez integratora', desc: 'Katalog, ceny i stany z BaseLinkera lub Sellasista. MAMS zapisuje treść i parametry do integratora; kanały wystawia integrator.', icon: Boxes },
  { id: 'direct', title: 'Bezpośrednio na platformie', desc: 'Klucze API klienta do Kauflanda, Allegro lub Amazona. MAMS publikuje i synchronizuje sam; katalog z pliku albo integratora.', icon: Globe },
];

function ModeSwitch({ value, onChange, compact = false }) {
  return (
    <div className={cn('grid gap-3', compact ? 'grid-cols-1' : 'sm:grid-cols-2')}>
      {MODES.map(o => {
        const Icon = o.icon; const on = value === o.id;
        return (
          <button key={o.id} type="button" onClick={() => onChange(o.id)} className={cn('flex items-start gap-3 text-left p-3.5 rounded-lg border transition-colors', on ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600' : 'border-ink-200 hover:border-ink-300 dark:border-ink-700 dark:hover:border-ink-600')}>
            <span className={cn('w-8 h-8 rounded-md inline-flex items-center justify-center flex-shrink-0', on ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400')}><Icon className="w-4 h-4" /></span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink-900 dark:text-ink-50">{o.title}</span>
              {!compact && <span className="block text-xs text-ink-500 dark:text-ink-400 mt-0.5 leading-snug">{o.desc}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Karta połączenia (używana w kreatorze i w edycji) ───────────────────────

function ConnectionCard(props) {
  if (props.system === 'allegro') return <AllegroConnectionCard {...props} />;
  return <KeyConnectionCard {...props} />;
}

async function callAllegroAuth(payload) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Brak sesji — zaloguj się ponownie.');
  const res = await fetch(ALLEGRO_AUTH_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

/**
 * INT-ALLEGRO (22.10): połączenie z kontem Allegro klienta przez DEVICE FLOW aplikacji MAMS — bez kluczy.
 * „Połącz” → kod + link do strony Allegro → klient loguje się na swoje konto i potwierdza kod → MAMS dostaje token
 * sprzedawcy (Vault). Środowisko: sandbox (test) albo produkcja (na razie tylko odczyt — wystawianie włącza właściciel MAMS).
 */
function AllegroConnectionCard({ tenantId, connection, onSaved, onRemoved }) {
  const meta = connection?.meta || {};
  const [env, setEnv] = useState(meta.env || meta.pending?.env || 'sandbox');
  const [flow, setFlow] = useState(null); // { connection_id, user_code, verification_uri, expires_at, interval }
  const [busy, setBusy] = useState(null);
  const pollRef = useRef(null);
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;
  const connected = connection?.status === 'ok' && !!meta.login;

  const stopPoll = useCallback(() => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } }, []);
  useEffect(() => stopPoll, [stopPoll]);

  const poll = useCallback((f) => {
    stopPoll();
    pollRef.current = setTimeout(async () => {
      try {
        const r = await callAllegroAuth({ action: 'poll', connection_id: f.connection_id });
        if (r.status === 'pending') { poll(r.slow_down ? { ...f, interval: f.interval + 5 } : f); return; }
        setFlow(null);
        if (r.status === 'ok') { toast.success(`Allegro połączone${r.login ? `: konto ${r.login}` : ''}`); savedRef.current?.(r); return; }
        toast.error(r.error || 'Autoryzacja Allegro nieudana');
        savedRef.current?.(r);
      } catch (e) {
        toast.error(e.message);
        if (Date.now() < f.expires_at) poll({ ...f, interval: Math.min(f.interval * 2, 30) }); else setFlow(null);
      }
    }, Math.max(3, f.interval || 5) * 1000);
  }, [stopPoll]);

  const start = async () => {
    setBusy('start');
    try {
      const r = await callAllegroAuth({ action: 'start', env, ...(connection ? { connection_id: connection.id } : { tenant_id: tenantId }) });
      const f = { connection_id: r.connection_id, user_code: r.user_code, verification_uri: r.verification_uri, expires_at: Date.now() + (r.expires_in || 3600) * 1000, interval: r.interval || 5 };
      setFlow(f);
      poll(f);
      try { window.open(r.verification_uri, '_blank', 'noopener'); } catch { /* blokada okien — link jest niżej */ }
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy('test');
    try {
      const r = await callAllegroAuth({ action: 'test', connection_id: connection.id });
      if (r.status === 'ok') toast.success(`Allegro działa${r.login ? ` — konto ${r.login}` : ''}`); else toast.error(r.error || 'Allegro: błąd');
      onSaved?.(r);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const cancel = () => { stopPoll(); setFlow(null); };
  const remove = async () => {
    stopPoll();
    if (!connection) return onRemoved?.();
    if (!window.confirm('Usunąć połączenie Allegro? MAMS straci dostęp do konta Allegro klienta (oferty na Allegro zostają).')) return;
    const { error } = await supabase.from('connections').delete().eq('id', connection.id);
    if (error) return toast.error(error.message);
    toast.success('Połączenie usunięte');
    onRemoved?.();
  };

  const envBtn = (id, label, hint) => (
    <button key={id} type="button" disabled={!!flow || !!busy} onClick={() => setEnv(id)}
      className={cn('flex-1 text-left px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-60', env === id ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600' : 'border-ink-200 hover:border-ink-300 dark:border-ink-700')}>
      <span className="block font-semibold text-ink-900 dark:text-ink-50">{label}</span>
      <span className="block text-2xs text-ink-500 dark:text-ink-400">{hint}</span>
    </button>
  );

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-4 flex items-center gap-3 border-b border-ink-100 dark:border-ink-800">
        <SystemMark system="allegro" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-ink-900 dark:text-ink-50">Allegro{connected ? <span className="ml-2 text-xs font-normal text-ink-500">{meta.env === 'prod' ? 'produkcja' : 'sandbox'}</span> : null}</div>
          <div className="mt-0.5"><ConnectionStatus status={connection?.status} testedAt={connection?.tested_at} /></div>
        </div>
        <Button size="sm" variant="ghost" onClick={remove} title="Usuń"><Trash2 className="w-4 h-4 text-ink-400 hover:text-danger" /></Button>
      </div>
      <CardBody className="px-5 py-4 grid gap-3">
        {!connected && !flow && (
          <div className="flex flex-col sm:flex-row gap-2">
            {envBtn('sandbox', 'Sandbox (test)', 'allegro.pl.allegrosandbox.pl — konto testowe, bez skutków')}
            {envBtn('prod', 'Produkcja', 'prawdziwe konto Allegro — na razie tylko odczyt')}
          </div>
        )}
        {flow && (
          <div className="rounded-lg border border-brand-200 bg-brand-50/60 dark:border-brand-800 dark:bg-brand-900/20 px-4 py-3 grid gap-2">
            <div className="text-sm text-ink-700 dark:text-ink-200">1. Otwórz stronę Allegro{env === 'sandbox' ? ' (sandbox)' : ''} i zaloguj się na konto klienta. 2. Potwierdź kod:</div>
            <div className="text-3xl font-extrabold tracking-[0.3em] text-ink-900 dark:text-ink-50 select-all">{flow.user_code}</div>
            <div className="flex flex-wrap items-center gap-2">
              <a href={flow.verification_uri} target="_blank" rel="noreferrer"><Button size="sm" leftIcon={<Globe className="w-3.5 h-3.5" />}>Otwórz stronę Allegro</Button></a>
              <span className="inline-flex items-center gap-1.5 text-xs text-ink-500 dark:text-ink-400"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> czekam na potwierdzenie…</span>
              <Button size="sm" variant="ghost" onClick={cancel}>Anuluj</Button>
            </div>
          </div>
        )}
        {connection?.status === 'error' && connection?.last_error && !flow && (
          <div className="flex items-start gap-2 text-sm text-danger-dark dark:text-danger bg-danger-light/50 dark:bg-danger/10 rounded-md px-3 py-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{connection.last_error}</span>
          </div>
        )}
        {connected && !flow && <ConnectionMeta system="allegro" meta={meta} />}
        {!flow && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={start} loading={busy === 'start'} leftIcon={<Plug className="w-3.5 h-3.5" />}>{connected ? 'Połącz ponownie' : 'Połącz z Allegro'}</Button>
            {connection && (connected || connection.status === 'error') && <Button size="sm" variant="secondary" onClick={test} loading={busy === 'test'}>Testuj</Button>}
            <span className="text-xs text-ink-400 dark:text-ink-500">Bez kluczy: klient loguje się na Allegro i wpisuje kod. Token trafia do Vault.</span>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function KeyConnectionCard({ tenantId, system, connection, onSaved, onRemoved }) {
  const def = SYSTEMS[system];
  // `__locked`: pola startują jako readOnly, odblokowują się przy pierwszym kliknięciu — to blokuje
  // Chrome, który inaczej wkleja do „tekst + hasło" zapisane dane logowania (zgłoszenie Maćka 14.10).
  const [secret, setSecret] = useState({ __locked: true });
  const [editing, setEditing] = useState(!connection);
  const [busy, setBusy] = useState(false);

  const hasSecretInput = def.fields.filter(f => f.type !== 'checkbox').every(f => (secret[f.key] ?? '').trim().length > 0);

  const test = async () => {
    setBusy(true);
    try {
      const payload = connection ? { connection_id: connection.id } : { tenant_id: tenantId, system, label: def.label };
      if (editing && hasSecretInput) { const { __locked, ...clean } = secret; payload.secret = clean; }
      const r = await callConnectionsTest(payload);
      if (r.status === 'ok') toast.success(`${def.label}: połączenie działa`);
      else toast.error(`${def.label}: ${r.error || 'błąd'}`);
      setSecret({ __locked: true });
      setEditing(false);
      onSaved?.(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!connection) return onRemoved?.();
    if (!window.confirm(`Usunąć połączenie ${def.label}? Kanały na tym połączeniu stracą dostęp.`)) return;
    const { error } = await supabase.from('connections').delete().eq('id', connection.id);
    if (error) return toast.error(error.message);
    toast.success('Połączenie usunięte');
    onRemoved?.();
  };

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-4 flex items-center gap-3 border-b border-ink-100 dark:border-ink-800">
        <SystemMark system={system} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-ink-900 dark:text-ink-50">{def.label}</div>
          <div className="mt-0.5"><ConnectionStatus status={connection?.status} testedAt={connection?.tested_at} /></div>
        </div>
        <div className="flex items-center gap-1.5">
          {connection && !editing && (
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)} leftIcon={<KeyRound className="w-3.5 h-3.5" />}>Zmień klucze</Button>
          )}
          <Button size="sm" variant="ghost" onClick={remove} title="Usuń"><Trash2 className="w-4 h-4 text-ink-400 hover:text-danger" /></Button>
        </div>
      </div>
      <CardBody className="px-5 py-4">
        {def.disabled ? (
          <p className="text-sm text-ink-500 dark:text-ink-400">{def.note}</p>
        ) : (
          <>
            {editing && <SecretFields system={system} values={secret} onChange={setSecret} disabled={busy} />}
            {connection?.status === 'error' && connection?.last_error && (
              <div className="mt-3 flex items-start gap-2 text-sm text-danger-dark dark:text-danger bg-danger-light/50 dark:bg-danger/10 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{connection.last_error}</span>
              </div>
            )}
            {!editing && <ConnectionMeta system={system} meta={connection?.meta} />}
            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" onClick={test} loading={busy} disabled={editing && !hasSecretInput} leftIcon={<Plug className="w-3.5 h-3.5" />}>
                {editing ? 'Zapisz i testuj połączenie' : 'Testuj ponownie'}
              </Button>
              {editing && connection && <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setSecret({ __locked: true }); }}>Anuluj</Button>}
              <span className="text-xs text-ink-400 dark:text-ink-500">Klucze trafiają prosto do Vault — nie są nigdzie wyświetlane.</span>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ─── Kanały ─────────────────────────────────────────────────────────────────

function ChannelsEditor({ tenantId, connections, channels, onChange, autoDefault = false }) {
  const [busy, setBusy] = useState(null);
  const [manual, setManual] = useState(false);
  const { candidates, unsupported, baseStale, baseEmpty } = useMemo(() => {
    const out = [];
    connections.forEach(c => channelsForSystem(c.system, c.meta).forEach(ch => out.push({ ...ch, connection_id: c.id, system: c.system })));
    // Klient przez integratora (BASE/Sellasist) sprzedaje na marketplace'ach, które wystawia integrator (v9/A5: z jego listy).
    const direct = new Set(out.map(x => `${x.platform}:${x.market}`));
    const unsup = [];
    let stale = false; let empty = false;
    connections.filter(c => c.system === 'baselinker' || c.system === 'sellasist').forEach(c => {
      const r = integratorChannels(c);
      if (r.stale) stale = true;
      if (c.system === 'baselinker' && !r.stale && r.items.length === 0) empty = true;
      r.items.forEach(ch => {
        const key = `${ch.platform}:${ch.market}`;
        if (!ch.supported) { unsup.push(ch); return; }
        if (direct.has(key)) return;
        direct.add(key);
        out.push(ch);
      });
    });
    // Ręcznie (awaryjnie): BASE bez listy integracji — kanał dodany ręcznie zapisze do domyślnego pola „<kod>_0”.
    if (manual) {
      connections.filter(c => c.system === 'baselinker').forEach(c => {
        [...MARKETS.map(m => ({ platform: 'kaufland', market: m.id, label: `Kaufland · ${m.label}` })),
          { platform: 'allegro', market: 'pl', label: 'Allegro · Polska' },
          { platform: 'amazon', market: 'de', label: 'Amazon · Niemcy' }].forEach(ch => {
          const key = `${ch.platform}:${ch.market}`;
          if (direct.has(key)) return;
          direct.add(key);
          out.push({ ...ch, label: `${ch.label} (przez BASE, ręcznie)`, connection_id: c.id, system: c.system, via_integrator: true });
        });
      });
    }
    // Kanały sprzedaży przez integratora (np. „Allegro · Polska (przez Sellasist)”) na górze, źródła katalogu (sellasist_channel) na końcu.
    const rank = (x) => (x.platform === 'sellasist_channel' || x.platform === 'base_channel' ? 2 : x.via_integrator ? 0 : 1);
    out.sort((a, b) => rank(a) - rank(b));
    return { candidates: out, unsupported: unsup, baseStale: stale, baseEmpty: empty };
  }, [connections, manual]);
  const hasBase = connections.some(c => c.system === 'baselinker');

  const find = (c) => channels.find(x => x.platform === c.platform && x.market === c.market);

  const toggle = async (cand) => {
    const existing = find(cand);
    setBusy(`${cand.platform}:${cand.market}`);
    try {
      if (existing) {
        const { error } = await supabase.from('channels').update({ enabled: !existing.enabled }).eq('id', existing.id);
        if (error) throw error;
      } else {
        const settings = { ...(cand.integrator_channel_id ? { integrator_channel_id: cand.integrator_channel_id } : {}), ...(cand.via_integrator ? { via_integrator: cand.system } : {}), ...(cand.base_code ? { base_integration: cand.base_code } : {}) };
        const { error } = await supabase.from('channels').insert({
          tenant_id: tenantId, connection_id: cand.connection_id, platform: cand.platform, market: cand.market,
          language: marketLanguage(cand.market), enabled: true, settings,
        });
        if (error) throw error;
      }
      await onChange?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  // Tor 1 → tor 2 (18.10 22:05): klient z Sellasist dostaje domyślnie kanał „Allegro · Polska (przez Sellasist)”.
  // Tylko w kreatorze nowego klienta (autoDefault). Tylko gdy takiego kanału jeszcze nie ma w ogóle (odznaczony przez użytkownika = wiersz istnieje → nie ruszamy). Raz na połączenie.
  const autoTried = useRef(new Set());
  useEffect(() => {
    const cand = candidates.find(c => c.system === 'sellasist' && c.platform === 'allegro' && c.market === 'pl');
    if (!autoDefault || !cand || find(cand) || autoTried.current.has(cand.connection_id) || busy) return;
    autoTried.current.add(cand.connection_id);
    toggle(cand);
  }, [candidates, channels, autoDefault]);

  if (!connections.length) {
    return (
      <div className="grid gap-3">
        <p className="text-sm text-ink-500 dark:text-ink-400">Najpierw dodaj i przetestuj połączenie — z niego wynikają dostępne kanały.</p>
        <LiveWritesPanel tenantId={tenantId} channels={channels} onChange={onChange} />
      </div>
    );
  }
  const baseNote = hasBase && (baseStale || baseEmpty) && (
    <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-3.5 py-2.5 text-xs flex flex-wrap items-center gap-2">
      <span className="flex-1 min-w-[16rem]">
        {baseStale
          ? 'Lista kanałów BASE jest pobierana przy teście połączenia — kliknij „Zapisz i testuj” przy połączeniu BaseLinker, żeby ją odświeżyć.'
          : 'W BASE tego klienta nie ma podpiętych integracji marketplace (Kaufland, Allegro…). Podepnij je w BASE i przetestuj połączenie ponownie.'}
      </span>
      {!manual && <button type="button" className="underline font-semibold" onClick={() => setManual(true)}>Dodaj kanał ręcznie</button>}
    </div>
  );
  if (!candidates.length) {
    return (
      <div className="grid gap-3">
        {baseNote || <p className="text-sm text-ink-500 dark:text-ink-400">Połączenia są, ale nie zwróciły kanałów. Przetestuj połączenie ponownie po konfiguracji integratora.</p>}
        {unsupported.length > 0 && <UnsupportedChannels items={unsupported} />}
        {/* Audyt 16.10: przełącznik „Zapis na żywo” musi być dostępny także, gdy lista kanałów jest pusta. */}
        <LiveWritesPanel tenantId={tenantId} channels={channels} onChange={onChange} />
      </div>
    );
  }
  return (
    <div className="grid gap-4">
    {baseNote}
    <div className="grid gap-2 sm:grid-cols-2">
      {candidates.map(c => {
        const ex = find(c);
        const on = !!ex?.enabled;
        const key = `${c.platform}:${c.market}`;
        return (
          <button
            key={key}
            type="button"
            disabled={busy === key}
            onClick={() => toggle(c)}
            className={cn(
              'flex items-center gap-3 px-3.5 py-3 rounded-lg border text-left transition-colors',
              on ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-700' : 'border-ink-200 hover:border-ink-300 dark:border-ink-700 dark:hover:border-ink-600',
            )}
          >
            <span className={cn('w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0', on ? 'bg-brand-600 border-brand-600' : 'border-ink-300 dark:border-ink-600')}>
              {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
            </span>
            <SystemMark system={c.system} size="sm" />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{c.label}</span>
              <span className="block text-2xs text-ink-500 dark:text-ink-400">rynek {c.market.toUpperCase()} · język {marketLanguage(c.market).toUpperCase()}{c.via_integrator ? ' · treść z MAMS, wystawia integrator' : ''}{c.accounts?.length ? ` · konto: ${c.accounts.slice(0, 2).join(', ')}${c.accounts.length > 2 ? '…' : ''}` : ''}{ex && !ex.enabled ? ' · wyłączony' : ''}</span>
            </span>
          </button>
        );
      })}
    </div>
    {unsupported.length > 0 && <UnsupportedChannels items={unsupported} />}
    <LiveWritesPanel tenantId={tenantId} channels={channels} onChange={onChange} />
    {channels.filter(c => c.enabled && c.platform === 'kaufland' && !c.settings?.via_integrator).map(c => (
      <OfferSettingsPanel key={c.id} tenantId={tenantId} channel={c} onChange={onChange} />
    ))}
    </div>
  );
}

/**
 * v9/B3: OFERTY dla klienta bez integratora (cena i stan idą z MAMS na Kaufland, `POST /units` po gotowej karcie).
 * Domyślnie wyłączone — klient z ofertami w integratorze (np. Sellasist) dostałby duplikaty.
 * Zapis: channels.settings.offer = { enabled, id_warehouse, id_shipping_group, handling_time, default_stock }.
 * Magazyny i grupy wysyłki czytamy z konta Kauflanda klienta (edge kaufland-categories, action offer_options).
 */
function OfferSettingsPanel({ tenantId, channel, onChange }) {
  const cur = channel.settings?.offer || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ enabled: !!cur.enabled, id_warehouse: cur.id_warehouse ?? '', id_shipping_group: cur.id_shipping_group ?? '', handling_time: cur.handling_time ?? 2, default_stock: cur.default_stock ?? 0, sellasist_account: channel.settings?.sellasist_link?.external_account_id ?? '' });
  const [opts, setOpts] = useState(null);
  const [optsErr, setOptsErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || opts) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(KAUFLAND_CATEGORIES_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ action: 'offer_options', tenant_id: tenantId, storefront: channel.market }) });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
        setOpts(d);
      } catch (e) { setOptsErr(e.message); setOpts({ warehouses: [], shipping_groups: [] }); }
    })();
  }, [open, opts, tenantId, channel.market]);
  const save = async () => {
    setSaving(true);
    try {
      const offer = {
        enabled: !!form.enabled,
        ...(form.id_warehouse ? { id_warehouse: Number(form.id_warehouse) } : {}),
        ...(form.id_shipping_group ? { id_shipping_group: Number(form.id_shipping_group) } : {}),
        handling_time: Math.max(1, Math.min(30, Number(form.handling_time) || 2)),
        default_stock: Math.max(0, Number(form.default_stock) || 0),
      };
      // T1.5: powiązanie oferty w Sellasist klienta (konto Kauflanda podpięte w Sellasist) — puste = bez powiązania.
      const acc = Number(form.sellasist_account);
      const { sellasist_link: _old, ...restSettings } = channel.settings || {};
      const settings = { ...restSettings, offer, ...(acc > 0 ? { sellasist_link: { external_account_id: acc } } : {}) };
      const { error } = await supabase.from('channels').update({ settings }).eq('id', channel.id);
      if (error) throw error;
      toast.success(offer.enabled ? 'Oferty włączone — po gotowej karcie MAMS wystawi cenę i stan (zapis na żywo kanału decyduje, czy naprawdę).' : 'Ustawienia ofert zapisane (oferty wyłączone).');
      await onChange?.();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const sel = 'w-full h-9 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 text-sm';
  return (
    <div className="rounded-lg border border-ink-200 dark:border-ink-700 p-3.5">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <span>
          <span className="block text-sm font-semibold text-ink-900 dark:text-ink-50">Oferty (cena i stan) · Kaufland {String(channel.market).toUpperCase()}</span>
          <span className="block text-2xs text-ink-500 dark:text-ink-400">{cur.enabled ? 'Włączone — MAMS wystawia ofertę po gotowej karcie.' : 'Wyłączone — ofertę robi klient albo jego integrator.'}</span>
        </span>
        <ChevronRight className={cn('w-4 h-4 text-ink-400 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
            Wystawiaj oferty z MAMS (tylko gdy klient NIE ma ofert w integratorze)
          </label>
          <div>
            <Label>Magazyn</Label>
            <select className={sel} value={form.id_warehouse} onChange={e => setForm(f => ({ ...f, id_warehouse: e.target.value }))}>
              <option value="">domyślny z konta Kauflanda</option>
              {(opts?.warehouses || []).map(w => <option key={w.id} value={w.id}>{w.name}{w.is_default ? ' (domyślny)' : ''}</option>)}
            </select>
          </div>
          <div>
            <Label>Grupa wysyłki</Label>
            <select className={sel} value={form.id_shipping_group} onChange={e => setForm(f => ({ ...f, id_shipping_group: e.target.value }))}>
              <option value="">domyślna z konta Kauflanda</option>
              {(opts?.shipping_groups || []).map(g => <option key={g.id} value={g.id}>{g.name}{g.is_default ? ' (domyślna)' : ''}</option>)}
            </select>
          </div>
          <div>
            <Label>Czas wysyłki (dni robocze)</Label>
            <Input type="number" min="1" max="30" value={form.handling_time} onChange={e => setForm(f => ({ ...f, handling_time: e.target.value }))} />
          </div>
          <div>
            <Label>Stan, gdy w danych nie ma kolumny stanu</Label>
            <Input type="number" min="0" value={form.default_stock} onChange={e => setForm(f => ({ ...f, default_stock: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <Label>Powiązanie w Sellasist — ID konta Kauflanda w Sellasist klienta (opcjonalnie)</Label>
            <Input type="number" min="0" placeholder="puste = bez powiązania" value={form.sellasist_account} onChange={e => setForm(f => ({ ...f, sellasist_account: e.target.value }))} />
            <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">Gdy klient obsługuje zamówienia w Sellasist: po utworzeniu oferty MAMS powiąże ją z produktem w Sellasist (po EAN), żeby zamówienia i stany szły przez Sellasist. Numer konta Kauflanda znajdziesz w Sellasist w ustawieniach integracji z Kauflandem.</p>
          </div>
          {optsErr && <p className="sm:col-span-2 text-2xs text-warning-dark dark:text-warning">Nie udało się pobrać magazynów/grup z Kauflanda: {optsErr}. Zostaną użyte domyślne z konta.</p>}
          <div className="sm:col-span-2 flex justify-end">
            <Button size="sm" loading={saving} onClick={save}>Zapisz ustawienia ofert</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function UnsupportedChannels({ items }) {
  return (
    <p className="text-2xs text-ink-500 dark:text-ink-400">
      W BASE są też: {items.map(i => i.label.replace(' (przez BASE)', '')).join(', ')} — MAMS jeszcze nie generuje treści pod te platformy.
    </p>
  );
}

/**
 * v9/A4: „Zapis na żywo” per kanał — bez niego publikacja/zapis do BASE to zawsze sucha próba.
 * Przełącza admin albo właściciel organizacji klienta (strażnik w bazie: channels_live_writes_guard).
 * Ukryte, dopóki baza nie ma kolumny channels.live_writes (migracja A4).
 */
function useCanManageLive(tenantId) {
  const [can, setCan] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user; // PERF-UX A6: bez żądania do GoTrue
      if (!user || !tenantId) return;
      const { data: p } = await supabase.from('profiles').select('is_admin').eq('id', user.id).maybeSingle();
      if (p?.is_admin) { if (alive) setCan(true); return; }
      const { data: t } = await supabase.from('tenants').select('org_id').eq('id', tenantId).maybeSingle();
      if (!t?.org_id) return;
      const { data: m } = await supabase.from('org_members').select('role').eq('org_id', t.org_id).eq('user_id', user.id).maybeSingle();
      if (alive) setCan(m?.role === 'owner');
    })().catch(() => {});
    return () => { alive = false; };
  }, [tenantId]);
  return can;
}

function LiveWritesPanel({ tenantId, channels, onChange }) {
  const canManage = useCanManageLive(tenantId);
  const [busy, setBusy] = useState(null);
  const enabled = channels.filter(c => c.enabled && Object.prototype.hasOwnProperty.call(c, 'live_writes'));
  if (!enabled.length) return null;
  const flip = async (ch) => {
    setBusy(ch.id);
    try {
      const { error } = await supabase.from('channels').update({ live_writes: !ch.live_writes }).eq('id', ch.id);
      if (error) throw error;
      toast.success(!ch.live_writes ? 'Zapis na żywo włączony — publikacja pójdzie na konto klienta.' : 'Zapis na żywo wyłączony — tylko suche próby.');
      await onChange?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-lg border border-ink-200 dark:border-ink-700 p-3.5">
      <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">Zapis na żywo (konto klienta)</div>
      <p className="text-2xs text-ink-500 dark:text-ink-400 mb-2.5">
        Wyłączony = publikacja i zapis do BASE/Sellasist robią tylko suchą próbę. {canManage ? 'Włączaj dopiero po udanej suchej próbie.' : 'Włącza właściciel organizacji albo administrator.'}
      </p>
      <div className="grid gap-1.5">
        {enabled.map(ch => (
          <div key={ch.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-ink-700 dark:text-ink-300">{(CHANNEL_PLATFORM_LABEL_SHORT[ch.platform] || ch.platform)} · {String(ch.market).toUpperCase()}{ch.settings?.via_integrator ? ` (przez ${ch.settings.via_integrator === 'baselinker' ? 'BASE' : 'Sellasist'})` : ''}</span>
            <button
              type="button"
              disabled={!canManage || busy === ch.id}
              onClick={() => flip(ch)}
              title={canManage ? '' : 'Włącza właściciel organizacji albo administrator'}
              className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50', ch.live_writes ? 'bg-amber-500' : 'bg-ink-300 dark:bg-ink-600')}
            >
              <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform', ch.live_writes ? 'translate-x-5' : 'translate-x-0.5')} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
const CHANNEL_PLATFORM_LABEL_SHORT = { kaufland: 'Kaufland', allegro: 'Allegro', amazon: 'Amazon', ebay: 'eBay', emag: 'eMAG', temu: 'Temu', sellasist_channel: 'Sellasist · sklep', base_channel: 'BaseLinker · katalog' };

// ─── Reguły klienta ─────────────────────────────────────────────────────────

const GENERIC_SAMPLES = [{ label: 'tani', price: 10 }, { label: 'typowy', price: 49.9 }, { label: 'droższy', price: 120 }, { label: 'drogi', price: 650 }];

/** Przykłady z katalogu klienta: najtańszy, typowy (mediana) i najdroższy produkt z ostatnich zleceń (batch_results.original_data). */
function useCatalogSamples(tenantId) {
  const [samples, setSamples] = useState([]);
  useEffect(() => {
    if (!tenantId) return undefined;
    let alive = true;
    (async () => {
      const { data: ords } = await supabase.from('orders').select('id').eq('tenant_id', tenantId).order('created_at', { ascending: false }).limit(5);
      const ids = (ords || []).map(o => o.id);
      if (!ids.length) return;
      const { data: rows } = await supabase.from('batch_results').select('original_data').in('order_id', ids).limit(1000);
      const items = [];
      (rows || []).forEach(r => {
        const od = r.original_data || {};
        const pk = Object.keys(od).find(k => /^(cena|price|cena_netto|cena netto|cena brutto|cena_brutto)$/i.test(k.trim())) || Object.keys(od).find(k => /cena|price/i.test(k));
        const nk = Object.keys(od).find(k => /^(nazwa|name|tytuł|title)$/i.test(k.trim())) || Object.keys(od).find(k => /nazw|name|tytu/i.test(k));
        const price = Number(String(od[pk] ?? '').replace(/\s/g, '').replace(',', '.'));
        if (pk && price > 0) items.push({ price, name: nk ? String(od[nk]).slice(0, 60) : '' });
      });
      if (!alive || items.length < 2) return;
      items.sort((a, b) => a.price - b.price);
      const cheap = items[0], typical = items[Math.floor(items.length / 2)], expensive = items[items.length - 1];
      const out = [{ label: 'najtańszy', ...cheap }, { label: 'typowy', ...typical }];
      if (expensive.price !== typical.price) out.push({ label: 'najdroższy', ...expensive });
      setSamples(out);
    })();
    return () => { alive = false; };
  }, [tenantId]);
  return samples;
}

const DEFAULT_RULE = { rate: { mode: 'nbp', value: 4.3 }, margin_pct: 10, margin_add: 0, margin_mode: 'sum', tiers: [], rounding: 'none', min_price: 0 };
const ROUNDINGS = [
  { id: 'none', label: 'bez zaokrąglenia' },
  { id: '0.99', label: 'do ,99' },
  { id: '0.90', label: 'do ,90' },
  { id: '0.05', label: 'do 5 groszy/centów' },
];

/** Ta sama arytmetyka, którą ma liczyć silnik (czat 1, T18). Trzymać w zgodzie. */
export function computePrice(rule, p, rate) {
  if (!(p > 0) || !(rate > 0)) return null;
  const base = p / rate;
  let pct = Number(rule.margin_pct) || 0, add = Number(rule.margin_add) || 0;
  const tiers = (rule.tiers || []).filter(t => t.up_to > 0).sort((a, b) => a.up_to - b.up_to);
  const tier = tiers.find(t => p <= t.up_to);
  if (tier) { pct = Number(tier.margin_pct) || 0; add = Number(tier.margin_add) || 0; }
  let v = rule.margin_mode === 'max' ? Math.max(base * (1 + pct / 100), base + add) : base * (1 + pct / 100) + add;
  if (rule.min_price > 0) v = Math.max(v, Number(rule.min_price));
  if (rule.rounding === '0.99') v = Math.max(0.99, Math.floor(v) + 0.99 - (v - Math.floor(v) < 0.5 && Math.floor(v) > 0 ? 1 : 0));
  else if (rule.rounding === '0.90') v = Math.max(0.9, Math.floor(v) + 0.9 - (v - Math.floor(v) < 0.4 && Math.floor(v) > 0 ? 1 : 0));
  else if (rule.rounding === '0.05') v = Math.round(v * 20) / 20;
  return Math.round(v * 100) / 100;
}

/** Czytelny opis reguły — zapisywany w `expression` (zgodność wsteczna + do raportów). */
function ruleToExpression(rule) {
  const r = rule.rate?.mode === 'fixed' ? String(rule.rate.value) : 'kurs_NBP';
  const pct = Number(rule.margin_pct) || 0, add = Number(rule.margin_add) || 0;
  const core = rule.margin_mode === 'max' ? `max(p/${r}*${(1 + pct / 100).toFixed(4)}, p/${r}+${add})` : `p/${r}*${(1 + pct / 100).toFixed(4)}+${add}`;
  const extra = [];
  if ((rule.tiers || []).length) extra.push(`progi: ${rule.tiers.map(t => `≤${t.up_to} zł → ${t.margin_pct}% + ${t.margin_add}`).join(', ')}`);
  if (rule.min_price > 0) extra.push(`min ${rule.min_price}`);
  if (rule.rounding && rule.rounding !== 'none') extra.push(`zaokr. ${rule.rounding}`);
  return extra.length ? `${core} [${extra.join('; ')}]` : core;
}

function useNbpRate(currency) {
  const [rate, setRate] = useState(null);
  const [date, setDate] = useState(null);
  useEffect(() => {
    if (!currency || currency === 'PLN') { setRate(1); setDate(null); return; }
    let alive = true;
    fetch(`https://api.nbp.pl/api/exchangerates/rates/a/${currency.toLowerCase()}/?format=json`)
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (alive && j?.rates?.[0]) { setRate(j.rates[0].mid); setDate(j.rates[0].effectiveDate); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [currency]);
  return { rate, date };
}

function NumberField({ label, value, onChange, suffix, step = '0.01', min = 0, className, hint }) {
  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <div className="relative">
        <Input type="number" inputMode="decimal" step={step} min={min} value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} className={cn(suffix && 'pr-12')} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-ink-400">{suffix}</span>}
      </div>
      {hint && <FieldHint>{hint}</FieldHint>}
    </div>
  );
}

function PriceRuleBuilder({ rule, currency, onChange, samples: catalogSamples }) {
  const { rate: nbp, date: nbpDate } = useNbpRate(currency);
  const set = (patch) => onChange({ ...rule, ...patch });
  const effRate = currency === 'PLN' ? 1 : rule.rate?.mode === 'fixed' ? Number(rule.rate.value) || 0 : nbp;
  const samples = catalogSamples?.length ? catalogSamples : GENERIC_SAMPLES;
  const fmt = (v) => v == null ? '—' : v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const addTier = () => set({ tiers: [...(rule.tiers || []), { up_to: 50, margin_pct: rule.margin_pct || 0, margin_add: rule.margin_add || 0 }] });
  const setTier = (i, patch) => set({ tiers: rule.tiers.map((t, k) => k === i ? { ...t, ...patch } : t) });
  const rmTier = (i) => set({ tiers: rule.tiers.filter((_, k) => k !== i) });

  return (
    <div className="grid gap-5">
      {currency !== 'PLN' && (
        <div>
          <Label>Kurs PLN → {currency}</Label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => set({ rate: { ...rule.rate, mode: 'nbp' } })} className={cn('text-left p-3 rounded-lg border text-sm', rule.rate?.mode !== 'fixed' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600' : 'border-ink-200 dark:border-ink-700')}>
              <span className="block font-semibold text-ink-900 dark:text-ink-50">Aktualny kurs NBP</span>
              <span className="block text-xs text-ink-500 dark:text-ink-400">{nbp ? `${fmt(nbp)} zł z ${nbpDate}` : 'pobieram…'} · silnik bierze kurs z dnia biegu</span>
            </button>
            <button type="button" onClick={() => set({ rate: { mode: 'fixed', value: rule.rate?.value || nbp || 4.3 } })} className={cn('text-left p-3 rounded-lg border text-sm', rule.rate?.mode === 'fixed' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600' : 'border-ink-200 dark:border-ink-700')}>
              <span className="block font-semibold text-ink-900 dark:text-ink-50">Stały kurs</span>
              <span className="block text-xs text-ink-500 dark:text-ink-400">np. 4,30 — bezpieczny bufor na wahania</span>
            </button>
          </div>
          {rule.rate?.mode === 'fixed' && <NumberField className="mt-2 max-w-[160px]" value={rule.rate.value} onChange={v => set({ rate: { mode: 'fixed', value: v } })} suffix="zł" step="0.01" />}
        </div>
      )}

      <div>
        <Label>Marża</Label>
        <div className="grid grid-cols-[1fr_1fr] gap-2">
          <NumberField value={rule.margin_pct} onChange={v => set({ margin_pct: v })} suffix="%" step="0.5" />
          <NumberField value={rule.margin_add} onChange={v => set({ margin_add: v })} suffix={`+ ${currency}`} step="0.01" />
        </div>
        <div className="mt-2 flex gap-2 text-xs">
          <button type="button" onClick={() => set({ margin_mode: 'sum' })} className={cn('px-2.5 py-1 rounded-full border', rule.margin_mode !== 'max' ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'border-ink-200 text-ink-600 dark:border-ink-700 dark:text-ink-300')}>procent + kwota (razem)</button>
          <button type="button" onClick={() => set({ margin_mode: 'max' })} className={cn('px-2.5 py-1 rounded-full border', rule.margin_mode === 'max' ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'border-ink-200 text-ink-600 dark:border-ink-700 dark:text-ink-300')}>procent albo kwota — co wyższe</button>
        </div>
        <FieldHint>{rule.margin_mode === 'max' ? 'Tanie produkty dostają co najmniej kwotę, drogie — procent (np. 10% albo co najmniej +1 €).' : 'Do ceny po kursie dodajemy procent, a potem kwotę.'}</FieldHint>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="mb-0">Inna marża dla tańszych produktów</Label>
          <Button size="xs" variant="ghost" onClick={addTier} leftIcon={<Plus className="w-3 h-3" />}>Dodaj próg</Button>
        </div>
        {(rule.tiers || []).length === 0 ? <FieldHint>Opcjonalnie: np. do 50 zł — 25% + 1 €, powyżej — marża główna.</FieldHint> : (
          <div className="mt-2 grid gap-2">
            {rule.tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[auto_1fr_1fr_1fr_auto] items-center gap-2 text-sm">
                <span className="text-ink-500 dark:text-ink-400 text-xs">cena źródłowa do</span>
                <NumberField value={t.up_to} onChange={v => setTier(i, { up_to: v })} suffix="zł" step="1" />
                <NumberField value={t.margin_pct} onChange={v => setTier(i, { margin_pct: v })} suffix="%" step="0.5" />
                <NumberField value={t.margin_add} onChange={v => setTier(i, { margin_add: v })} suffix={`+ ${currency}`} />
                <Button size="sm" variant="ghost" onClick={() => rmTier(i)}><Trash2 className="w-4 h-4 text-ink-400" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Zaokrąglenie</Label>
          <select value={rule.rounding || 'none'} onChange={e => set({ rounding: e.target.value })} className="w-full h-10 rounded-md text-sm border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-100 px-3">
            {ROUNDINGS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <NumberField label="Cena minimalna" value={rule.min_price} onChange={v => set({ min_price: v })} suffix={currency} hint="0 = brak" />
      </div>

      <div className="rounded-lg bg-ink-50 dark:bg-ink-800/50 p-3">
        <div className="text-2xs uppercase tracking-wide font-bold text-ink-500 dark:text-ink-400 mb-2">Podgląd {effRate ? `(kurs ${fmt(effRate)})` : '(brak kursu)'}{catalogSamples?.length ? ' · produkty tego klienta' : ''}</div>
        <div className={cn('grid gap-2', samples.length > 3 ? 'grid-cols-4' : 'grid-cols-3')}>
          {samples.map(sm => (
            <div key={sm.label + sm.price} className="min-w-0 text-center">
              <div className="text-2xs text-ink-500 dark:text-ink-400 truncate" title={sm.name || sm.label}>{sm.label}</div>
              {sm.name && <div className="text-2xs text-ink-700 dark:text-ink-300 truncate" title={sm.name}>{sm.name}</div>}
              <div className="text-xs text-ink-500 dark:text-ink-400">{fmt(sm.price)} zł</div>
              <div className="text-sm font-bold text-ink-900 dark:text-ink-50 tabular-nums">{fmt(computePrice(rule, sm.price, effRate))} {currency}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Reguła ceny — tylko najważniejsze pola, bez przykładów (są w „Więcej reguł"). */
function QuickPriceRule({ rule, currency, onChange, onMore }) {
  const { rate: nbp } = useNbpRate(currency);
  const set = (patch) => onChange({ ...rule, ...patch });
  const fmt = (v) => v == null ? '' : v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const seg = (active) => cn('flex-1 px-3 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors', active ? 'bg-white dark:bg-ink-700 shadow-sm font-semibold text-ink-900 dark:text-ink-50' : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-200');
  const tiers = (rule.tiers || []).length;
  return (
    <div className="grid gap-4">
      {currency !== 'PLN' && (
        <div>
          <Label>Kurs PLN → {currency}</Label>
          <div className="flex gap-2 items-center">
            <div className="flex flex-1 p-1 rounded-lg bg-ink-100 dark:bg-ink-800">
              <button type="button" className={seg(rule.rate?.mode !== 'fixed')} onClick={() => set({ rate: { ...rule.rate, mode: 'nbp' } })}>NBP{nbp ? ` · ${fmt(nbp)}` : ''}</button>
              <button type="button" className={seg(rule.rate?.mode === 'fixed')} onClick={() => set({ rate: { mode: 'fixed', value: rule.rate?.value || 4.3 } })}>Stały</button>
            </div>
            {rule.rate?.mode === 'fixed' && <NumberField className="w-28" value={rule.rate.value} onChange={v => set({ rate: { mode: 'fixed', value: v } })} suffix="zł" />}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Marża" value={rule.margin_pct} onChange={v => set({ margin_pct: v })} suffix="%" step="0.5" />
        <NumberField label={rule.margin_mode === 'max' ? 'albo kwota' : 'plus kwota'} value={rule.margin_add} onChange={v => set({ margin_add: v })} suffix={currency} />
        <NumberField label="Cena minimalna" value={rule.min_price} onChange={v => set({ min_price: v })} suffix={currency} />
        <div>
          <Label>Zaokrąglenie</Label>
          <select value={rule.rounding || 'none'} onChange={e => set({ rounding: e.target.value })} className="w-full h-10 rounded-md text-sm border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-100 px-3">
            {ROUNDINGS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex p-0.5 rounded-md bg-ink-100 dark:bg-ink-800 text-xs">
          <button type="button" onClick={() => set({ margin_mode: 'max' })} className={cn('px-2.5 py-1 rounded', rule.margin_mode === 'max' ? 'bg-white dark:bg-ink-700 shadow-sm font-semibold text-ink-900 dark:text-ink-50' : 'text-ink-500 dark:text-ink-400')}>co wyższe</button>
          <button type="button" onClick={() => set({ margin_mode: 'sum' })} className={cn('px-2.5 py-1 rounded', rule.margin_mode !== 'max' ? 'bg-white dark:bg-ink-700 shadow-sm font-semibold text-ink-900 dark:text-ink-50' : 'text-ink-500 dark:text-ink-400')}>razem</button>
        </div>
        <Button size="sm" variant="ghost" onClick={onMore} leftIcon={<SlidersHorizontal className="w-3.5 h-3.5" />}>Więcej{tiers ? ` · ${tiers}` : ''}</Button>
      </div>
    </div>
  );
}

function MoreRulesModal({ title, rule, currency, onChange, samples, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white dark:bg-ink-900 rounded-xl shadow-2xl w-full max-w-3xl my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 dark:border-ink-700">
          <div>
            <div className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</div>
            <div className="text-2xs text-ink-500 dark:text-ink-400">Zmiany od razu widać w podglądzie. Zapisujesz przyciskiem „Zapisz reguły" pod formularzem.</div>
          </div>
          <Button size="sm" onClick={onClose}>Gotowe</Button>
        </div>
        <div className="px-6 py-5"><PriceRuleBuilder rule={rule} currency={currency} onChange={onChange} samples={samples} /></div>
      </div>
    </div>
  );
}

function RulesForm({ tenant, priceRule, onSaved, onDirtyChange, samples, saveRef, compact = false }) {
  const [weee, setWeee] = useState(tenant.weee_number || '');
  const [battg, setBattg] = useState(tenant.rules?.battg_number || ''); // silnik czyta tenants.rules.battg_number (T17 czatu 1)
  const [notes, setNotes] = useState(tenant.notes || '');
  const [currency, setCurrency] = useState(priceRule?.currency || 'EUR');
  const [rule, setRule] = useState(() => ({ ...DEFAULT_RULE, ...(priceRule?.rule && Object.keys(priceRule.rule).length ? priceRule.rule : {}) }));
  // Nowy klient: reguła włączona od razu (kurs NBP, 10%) — wcześniej była schowana za checkboxem i łatwo ją było pominąć.
  const [enabled, setEnabled] = useState(true);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedSnap, setSavedSnap] = useState(() => JSON.stringify({ weee: tenant.weee_number || '', battg: tenant.rules?.battg_number || '', notes: tenant.notes || '', currency: priceRule?.currency || 'EUR', rule: priceRule ? (priceRule.rule || null) : null, enabled: !!priceRule }));
  const snap = JSON.stringify({ weee, battg, notes, currency, rule: enabled ? rule : null, enabled });
  const dirty = snap !== savedSnap;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const save = async () => {
    setBusy(true);
    try {
      const nextRules = { ...(tenant.rules || {}) };
      if (battg.trim()) nextRules.battg_number = battg.trim(); else delete nextRules.battg_number;
      const { error: e1 } = await supabase.from('tenants').update({ weee_number: weee.trim() || null, rules: nextRules, notes: notes.trim() || null }).eq('id', tenant.id);
      invalidate('clients:');
      if (e1) throw e1;
      if (enabled) {
        const row = { tenant_id: tenant.id, channel_id: null, expression: ruleToExpression(rule), currency: currency || 'EUR', rule };
        const { error: e2 } = priceRule
          ? await supabase.from('price_rules').update(row).eq('id', priceRule.id)
          : await supabase.from('price_rules').insert(row);
        if (e2) throw e2;
      } else if (priceRule) {
        const { error: e3 } = await supabase.from('price_rules').delete().eq('id', priceRule.id);
        if (e3) throw e3;
      }
      setSavedSnap(snap);
      toast.success('Reguły zapisane');
      await onSaved?.();
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (saveRef) saveRef.current = save;

  return (
    <div className="grid gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="mb-0">Cena</Label>
          {!enabled && <FieldHint className="mt-0">Bez reguły cena idzie 1:1 po kursie NBP.</FieldHint>}
        </div>
        <div className="flex items-center gap-2">
          <select value={currency} onChange={e => setCurrency(e.target.value)} className="h-9 rounded-md text-sm border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-100 px-2">
            {['EUR', 'PLN', 'CZK'].map(c => <option key={c}>{c}</option>)}
          </select>
          {enabled
            ? <button type="button" onClick={() => setEnabled(false)} className="text-xs text-ink-400 hover:text-ink-700 dark:hover:text-ink-200 underline-offset-2 hover:underline">bez reguły</button>
            : <Button size="sm" variant="secondary" onClick={() => setEnabled(true)}>Ustaw regułę</Button>}
        </div>
      </div>
      {enabled && <QuickPriceRule rule={rule} currency={currency} onChange={setRule} onMore={() => setMore(true)} />}
      {enabled && more && <MoreRulesModal title={`Reguła ceny — ${tenant.name}`} rule={rule} currency={currency} onChange={setRule} samples={samples} onClose={() => setMore(false)} />}
      <div>
        <Label>Numer WEEE (DE)</Label>
        <Input value={weee} onChange={e => setWeee(e.target.value)} placeholder="DE 12345678" />
        {!compact && <FieldHint>Bez numeru oferty elektroniki czekają na klienta.</FieldHint>}
      </div>
      <div>
        <Label>Numer BattG (DE, baterie)</Label>
        <Input value={battg} onChange={e => setBattg(e.target.value)} placeholder="DE 87654321" />
        {!compact && <FieldHint>Rejestr baterii (stiftung ear). Bez numeru produkty z bateriami czekają na klienta.</FieldHint>}
      </div>
      {!compact && <div>
        <Label>Notatki</Label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          className="w-full rounded-md text-sm border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-100 px-3.5 py-2.5 outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10"
          placeholder="Źródło katalogu (np. plik IdoSell), ustalenia z klientem…"
        />
      </div>}
      <div className="flex items-center gap-3">
        <Button onClick={save} loading={busy} disabled={!dirty} leftIcon={<Save className="w-4 h-4" />}>Zapisz reguły</Button>
        {dirty && <span className="text-xs text-warning-dark dark:text-warning font-medium">niezapisane zmiany</span>}
      </div>
    </div>
  );
}


function RulesCard({ tenant, priceRule, onSaved }) {
  const samples = useCatalogSamples(tenant.id);
  // key: po zapisie formularz startuje od świeżych danych.
  const formKey = `${priceRule?.id || 'none'}:${priceRule?.updated_at || ''}:${JSON.stringify(priceRule?.rule || null)}:${tenant.weee_number || ''}:${tenant.rules?.battg_number || ''}`;
  return (
    <Card>
      <CardHeader><CardTitle>Reguły</CardTitle></CardHeader>
      <CardBody>
        <RulesForm key={formKey} tenant={tenant} priceRule={priceRule} samples={samples} compact onSaved={onSaved} />
      </CardBody>
    </Card>
  );
}

// ─── Producenci (GPSR) — T16-UI ─────────────────────────────────────────────

const EMPTY_MANUF = { name: '', address: '', email: '', phone: '', form_url: '', country: '' };
// 23.10 (t74): GPSR dopuszcza formularz kontaktowy zamiast e-maila; telefon opcjonalny (Sellasist go chce).
const gpsrComplete = (m) => !!(m.name?.trim() && m.address?.trim() && (m.email?.trim() || m.form_url?.trim()));

function ManufacturerRow({ row, onSave, onDelete }) {
  const [m, setM] = useState(row);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setM(row); }, [row]);
  const dirty = ['name', 'address', 'email', 'country', 'phone', 'form_url'].some(k => (m[k] || '') !== (row[k] || ''));
  const save = async () => { setBusy(true); await onSave(m); setBusy(false); };
  const ok = gpsrComplete(m);
  return (
    <tr className={cn(!ok && 'bg-warning-light/30 dark:bg-warning/5')}>
      <td className="px-3 py-2 align-top"><Input value={m.name} onChange={e => setM({ ...m, name: e.target.value })} placeholder="Nazwa producenta" className="h-9" /></td>
      <td className="px-3 py-2 align-top"><Input value={m.address || ''} onChange={e => setM({ ...m, address: e.target.value })} placeholder="Ulica, kod, miasto" className="h-9" /></td>
      <td className="px-3 py-2 align-top"><Input value={m.email || ''} onChange={e => setM({ ...m, email: e.target.value })} placeholder="kontakt@…" className="h-9" type="email" /></td>
      <td className="px-3 py-2 align-top"><Input value={m.phone || ''} onChange={e => setM({ ...m, phone: e.target.value })} placeholder="+48 …" className="h-9" title="Telefon producenta — Sellasist wymaga go przy danych GPSR" /></td>
      <td className="px-3 py-2 align-top"><Input value={m.form_url || ''} onChange={e => setM({ ...m, form_url: e.target.value })} placeholder="https://…/kontakt" className="h-9" title="Formularz kontaktowy — GPSR dopuszcza go zamiast e-maila" /></td>
      <td className="px-3 py-2 align-top w-20"><Input value={m.country || ''} onChange={e => setM({ ...m, country: e.target.value.toUpperCase().slice(0, 2) })} placeholder="DE" className="h-9 text-center" /></td>
      <td className="px-3 py-2 align-top whitespace-nowrap">
        {ok ? <Badge variant="success" size="sm">komplet</Badge> : <Badge variant="warning" size="sm">brakuje danych</Badge>}
      </td>
      <td className="px-3 py-2 align-top whitespace-nowrap text-right">
        {(dirty || !row.id) && <Button size="sm" onClick={save} loading={busy} disabled={!m.name?.trim()}>{row.id ? 'Zapisz' : 'Dodaj'}</Button>}
        {row.id && <Button size="sm" variant="ghost" onClick={() => onDelete(row)} title="Usuń"><Trash2 className="w-4 h-4 text-ink-400 hover:text-danger" /></Button>}
      </td>
    </tr>
  );
}

function ManufacturersCard({ tenantId }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [paste, setPaste] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('manufacturers').select('*').eq('tenant_id', tenantId).order('name');
    if (error) toast.error(error.message);
    setRows(data || []);
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const save = async (m) => {
    const payload = { tenant_id: tenantId, name: m.name.trim(), address: m.address?.trim() || null, email: m.email?.trim() || null, phone: m.phone?.trim() || null, form_url: m.form_url?.trim() || null, country: m.country?.trim() || null, gpsr_ok: gpsrComplete(m) };
    const { error } = m.id ? await supabase.from('manufacturers').update(payload).eq('id', m.id) : await supabase.from('manufacturers').insert(payload);
    if (error) return toast.error(error.code === '23505' ? 'Taki producent już jest na liście.' : error.message);
    toast.success(m.id ? 'Producent zapisany' : 'Producent dodany');
    setAdding(false); load();
  };
  const remove = async (m) => {
    if (!window.confirm(`Usunąć producenta „${m.name}"?`)) return;
    const { error } = await supabase.from('manufacturers').delete().eq('id', m.id);
    if (error) return toast.error(error.message);
    load();
  };
  // Automat: nazwy producentów z faktów ostatnich biegów klienta (klucze `producent` / `marka`).
  // Łączy pisownie różniące się wielkością liter (AKS ZIELONKA = AKS Zielonka), wybiera najczęstszą.
  const [syncing, setSyncing] = useState(false);
  const importFromCatalog = async () => {
    setSyncing(true);
    try {
      const { data: ords } = await supabase.from('orders').select('id').eq('tenant_id', tenantId).limit(200);
      const ids = (ords || []).map(o => o.id);
      if (!ids.length) { toast.info('Ten klient nie ma jeszcze zleceń — uruchom zlecenie, wtedy producenci wczytają się sami.'); return; }
      const { data: facts, error } = await supabase.from('product_facts').select('value').in('order_id', ids).in('key', ['producent', 'marka']).limit(20000);
      if (error) throw error;
      const byKey = new Map();
      (facts || []).forEach(f => {
        const raw = String(f.value || '').replace(/\s*\[\d+\]\s*$/, '').trim();
        if (raw.length < 2) return;
        const k = raw.toLowerCase();
        const e = byKey.get(k) || { spellings: new Map(), total: 0 };
        e.spellings.set(raw, (e.spellings.get(raw) || 0) + 1); e.total++;
        byKey.set(k, e);
      });
      const existing = new Set((rows || []).map(r => r.name.toLowerCase()));
      const fresh = [...byKey.entries()].filter(([k]) => !existing.has(k)).map(([, e]) => [...e.spellings.entries()].sort((a, b) => b[1] - a[1])[0][0]);
      if (!fresh.length) { toast.info(`Z katalogu wynika ${byKey.size} producentów — wszyscy już są na liście.`); return; }
      const { error: insErr } = await supabase.from('manufacturers').insert(fresh.map(name => ({ tenant_id: tenantId, name, gpsr_ok: false })));
      if (insErr) throw insErr;
      toast.success(`Dodano ${fresh.length} producentów z katalogu (${byKey.size} łącznie). Uzupełnij adresy i e-maile.`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSyncing(false);
    }
  };

  // Automat krok 2: adresy i e-maile z konta Kauflanda klienta (producenci już tam zarejestrowani).
  const [kSyncing, setKSyncing] = useState(false);
  const fillFromKaufland = async () => {
    setKSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(MANUFACTURERS_SYNC_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ tenant_id: tenantId, only_missing: true }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const parts = [`sprawdzono ${d.checked}`, `uzupełniono ${d.filled}`];
      if (d.no_contact?.length) parts.push(`bez kontaktu na koncie: ${d.no_contact.length}`);
      if (d.unknown?.length) parts.push(`nieznani Kauflandowi: ${d.unknown.join(', ')}`);
      (d.filled ? toast.success : toast.info)(`Kaufland: ${parts.join(' · ')}`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setKSyncing(false);
    }
  };

  // Plan v6 R4: kontakty GPSR z rejestru → konto Kauflanda (manufacturers-sync v2, action push). Najpierw sucha próba.
  const [pushing, setPushing] = useState(false);
  const [pushPlan, setPushPlan] = useState(null);
  const pushToKaufland = async (dryRun) => {
    setPushing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(MANUFACTURERS_SYNC_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ tenant_id: tenantId, action: 'push', dry_run: dryRun }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setPushPlan(d);
      const n = (d.items || []).filter(i => i.outcome === (dryRun ? 'dry_run' : 'sent')).length;
      (n ? toast.success : toast.info)(dryRun ? `Do wysłania na Kaufland: ${n} producentów` : `Wysłano na Kaufland: ${n} producentów`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPushing(false);
    }
  };
  const PUSH_LABEL = { dry_run: 'do wysłania', sent: 'wysłano', failed: 'błąd', already_on_kaufland: 'już jest na Kauflandzie', unknown_manufacturer: 'Kaufland nie zna producenta', address_unparsed: 'adres do poprawy (ulica nr, kod miasto)' };

  // Automat krok 3: propozycje adresów przez AI (web search) — z pewnością; zapis dopiero po „Przyjmij".
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const suggestAddresses = async () => {
    setSuggesting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(MANUFACTURERS_SUGGEST_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ tenant_id: tenantId, limit: 6 }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      if (!d.suggestions?.length) { toast.info('Wszyscy producenci mają już komplet — nie ma czego szukać.'); return; }
      setSuggestions(d.suggestions);
      const hi = d.suggestions.filter(s => s.confidence === 'high').length;
      toast.success(`Agent sprawdził ${d.suggestions.length} producentów · ${hi} z wysoką pewnością${d.search === 'ddg' ? ' · bez klucza wyszukiwarki (Brave) — szukał po domenach i KRS' : ''}. Przejrzyj i przyjmij.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSuggesting(false);
    }
  };
  const acceptSuggestion = async (s) => {
    const row = (rows || []).find(r => r.name.toLowerCase() === s.name.toLowerCase());
    if (!row) return toast.error(`Nie ma już producenta „${s.name}" na liście.`);
    const merged = { ...row, address: row.address || s.address || null, email: row.email || s.email || null, country: row.country || s.country || null };
    const { error } = await supabase.from('manufacturers').update({ address: merged.address, email: merged.email, country: merged.country, gpsr_ok: gpsrComplete(merged) }).eq('id', row.id);
    if (error) return toast.error(error.message);
    setSuggestions(prev => (prev || []).filter(x => x.name !== s.name));
    load();
  };
  const acceptAllHigh = async () => {
    const hi = (suggestions || []).filter(s => s.confidence === 'high');
    for (const s of hi) await acceptSuggestion(s); // eslint-disable-line no-await-in-loop
    toast.success(`Przyjęto ${hi.length} z wysoką pewnością`);
  };

  const importNames = async () => {
    const names = [...new Set(paste.split(/\r?\n/).map(x => x.trim()).filter(Boolean))];
    if (!names.length) return;
    const existing = new Set((rows || []).map(r => r.name.toLowerCase()));
    const fresh = names.filter(n => !existing.has(n.toLowerCase()));
    if (!fresh.length) { toast.info('Wszystkie nazwy już są na liście.'); return; }
    const { error } = await supabase.from('manufacturers').insert(fresh.map(name => ({ tenant_id: tenantId, name, gpsr_ok: false })));
    if (error) return toast.error(error.message);
    toast.success(`Dodano ${fresh.length} producentów — uzupełnij adresy i e-maile`);
    setPaste(''); setPasteOpen(false); load();
  };

  const missing = (rows || []).filter(r => !gpsrComplete(r)).length;
  const visible = (rows || []).filter(r => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>Producenci (GPSR)</CardTitle>
          <CardDescription>Kaufland wymaga adresu i e-maila producenta dla każdej oferty. Brak = oferta czeka na klienta. Na konto Kauflanda zapisuje je silnik.</CardDescription>
        </div>
        {rows && rows.length > 0 && (
          <Badge variant={missing ? 'warning' : 'success'} size="md">{missing ? `${missing} bez kompletu` : 'wszyscy kompletni'} · {rows.length}</Badge>
        )}
      </CardHeader>
      <CardBody className="px-0 py-0">
        {rows == null ? <div className="p-5"><SkeletonRow /></div> : (
          <>
            {rows.length > 5 && (
              <div className="px-5 pt-4"><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Szukaj producenta…" className="h-9 max-w-xs" /></div>
            )}
            {(rows.length > 0 || adding) && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm mt-2">
                  <thead className="text-2xs uppercase tracking-wide text-ink-500 dark:text-ink-400">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold">Producent</th>
                      <th className="text-left px-3 py-2 font-bold">Adres</th>
                      <th className="text-left px-3 py-2 font-bold">E-mail</th>
                      <th className="text-left px-3 py-2 font-bold">Telefon</th>
                      <th className="text-left px-3 py-2 font-bold">Formularz</th>
                      <th className="text-left px-3 py-2 font-bold">Kraj</th>
                      <th className="text-left px-3 py-2 font-bold">GPSR</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
                    {visible.map(r => <ManufacturerRow key={r.id} row={r} onSave={save} onDelete={remove} />)}
                    {adding && <ManufacturerRow row={EMPTY_MANUF} onSave={save} onDelete={() => setAdding(false)} />}
                  </tbody>
                </table>
              </div>
            )}
            {rows.length === 0 && !adding && (
              <p className="px-5 pt-4 text-sm text-ink-500 dark:text-ink-400">Jeszcze nikogo nie ma. Kliknij „Wczytaj z katalogu" — nazwy wezmą się z ostatnich zleceń klienta.</p>
            )}
            {pasteOpen && (
              <div className="px-5 pt-4">
                <textarea value={paste} onChange={e => setPaste(e.target.value)} rows={5} placeholder={'Jedna nazwa w linii, np.\nAKS Zielonka\nBosch\nMakita'}
                  className="w-full rounded-md text-sm border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-100 px-3.5 py-2.5 outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10" />
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={importNames} disabled={!paste.trim()}>Dodaj z listy</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setPasteOpen(false); setPaste(''); }}>Anuluj</Button>
                </div>
              </div>
            )}
            {suggestions && suggestions.length > 0 && (
              <div className="mx-5 mt-4 rounded-lg border border-brand-200 dark:border-brand-800 bg-brand-50/50 dark:bg-brand-900/10">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-brand-100 dark:border-brand-800">
                  <div className="text-sm font-medium text-ink-900 dark:text-ink-50">Propozycje AI — {suggestions.length} · nic nie zapisze się bez „Przyjmij"</div>
                  <div className="flex gap-2">
                    {suggestions.some(s => s.confidence === 'high') && <Button size="xs" onClick={acceptAllHigh}>Przyjmij wszystkie z wysoką pewnością</Button>}
                    <Button size="xs" variant="ghost" onClick={() => setSuggestions(null)}>Zamknij</Button>
                  </div>
                </div>
                <div className="divide-y divide-brand-100 dark:divide-brand-800">
                  {suggestions.map(s => (
                    <div key={s.name} className="px-4 py-3 grid gap-1 sm:grid-cols-[1fr_auto] items-start text-sm">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-ink-900 dark:text-ink-50">{s.name}</span>
                          <Badge size="sm" variant={s.confidence === 'high' ? 'success' : s.confidence === 'medium' ? 'warning' : 'danger'}>{s.confidence === 'high' ? 'wysoka pewność' : s.confidence === 'medium' ? 'średnia pewność' : 'niska pewność'}</Badge>
                          {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="text-2xs text-brand-600 dark:text-brand-400 underline truncate max-w-[16rem]">{s.source_url.replace(/^https?:\/\//, '')}</a>}
                          {s.steps != null && <span className="text-2xs text-ink-400">{s.steps} krok{s.steps === 1 ? '' : 'ów'}</span>}
                        </div>
                        {s.legal_name && s.legal_name.toLowerCase() !== s.name.toLowerCase() && <div className="text-xs text-ink-600 dark:text-ink-400">nazwa prawna: <b>{s.legal_name}</b></div>}
                        <div className="text-ink-700 dark:text-ink-300 mt-0.5">{s.address || <span className="text-ink-400">brak adresu</span>} · {s.email || <span className="text-ink-400">brak e-maila</span>} · {s.country || <span className="text-ink-400">kraj?</span>}</div>
                        {s.note && <div className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">{s.note}</div>}
                      </div>
                      <Button size="xs" variant={s.confidence === 'high' ? 'primary' : 'outline'} disabled={!s.address && !s.email && !s.country} onClick={() => acceptSuggestion(s)}>Przyjmij</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {pushPlan && (
              <div className="mx-5 mt-4 rounded-lg border border-ink-200 dark:border-ink-700 text-sm">
                <div className="flex items-center justify-between px-4 py-2 border-b border-ink-100 dark:border-ink-800">
                  <span className="font-medium text-ink-900 dark:text-ink-50">Kontakty GPSR → Kaufland {pushPlan.dry_run ? '(sucha próba)' : ''}</span>
                  <div className="flex gap-2">
                    {pushPlan.dry_run && (pushPlan.items || []).some(i => i.outcome === 'dry_run') && <Button size="xs" loading={pushing} onClick={() => pushToKaufland(false)}>Wyślij naprawdę</Button>}
                    <Button size="xs" variant="ghost" onClick={() => setPushPlan(null)}>Zamknij</Button>
                  </div>
                </div>
                {(pushPlan.items || []).map(i => (
                  <div key={i.name} className="px-4 py-1.5 flex flex-wrap gap-2 text-xs border-b last:border-0 border-ink-100 dark:border-ink-800">
                    <b className="text-ink-900 dark:text-ink-50">{i.name}</b>
                    <span className={cn(i.outcome === 'failed' || i.outcome === 'address_unparsed' ? 'text-danger' : 'text-ink-500 dark:text-ink-400')}>{PUSH_LABEL[i.outcome] || i.outcome}{i.status ? ` · HTTP ${i.status}` : ''}</span>
                    {i.payload && <span className="text-ink-400 truncate">{i.payload.postal_address.street} {i.payload.postal_address.house_number}, {i.payload.postal_address.postal_code} {i.payload.postal_address.city} ({i.payload.postal_address.country_code}) · {i.payload.contact_info.email_address}</span>}
                  </div>
                ))}
                {pushPlan.dry_run && !pushPlan.writes_enabled && <div className="px-4 py-2 text-2xs text-warning-dark dark:text-warning">Zapis na żywo kanału Kaufland jest wyłączony — „Wyślij naprawdę" zadziała po włączeniu „Zapisu na żywo” w Kanałach (właściciel organizacji albo admin).</div>}
              </div>
            )}
            <div className="px-5 py-4 flex flex-wrap gap-2 items-center">
              <Button size="sm" onClick={importFromCatalog} loading={syncing} leftIcon={<RefreshCw className="w-3.5 h-3.5" />}>Wczytaj z katalogu</Button>
              {rows && rows.length > 0 && <Button size="sm" variant="outline" onClick={fillFromKaufland} loading={kSyncing}>Uzupełnij z konta Kauflanda</Button>}
              {rows && rows.some(r => r.gpsr_ok) && <Button size="sm" variant="outline" onClick={() => pushToKaufland(true)} loading={pushing}>Wyślij kontakty na Kaufland…</Button>}
              {missing > 0 && <Button size="sm" variant="outline" onClick={suggestAddresses} loading={suggesting} leftIcon={<Sparkles className="w-3.5 h-3.5" />}>{suggesting ? 'Agent szuka (do 2 min)…' : `Zaproponuj adresy (agent AI)${missing > 6 ? ` · ${Math.min(6, missing)} z ${missing}` : ''}`}</Button>}
              {!adding && <Button size="sm" variant="secondary" onClick={() => setAdding(true)} leftIcon={<Plus className="w-3.5 h-3.5" />}>Dodaj ręcznie</Button>}
              {!pasteOpen && <Button size="sm" variant="ghost" onClick={() => setPasteOpen(true)}>Wklej listę nazw</Button>}
              <span className="text-2xs text-ink-400 dark:text-ink-500">Kolejność: „Wczytaj z katalogu" (nazwy ze zleceń) → „Uzupełnij z konta Kauflanda" (już zarejestrowani) → „Zaproponuj adresy (agent AI)" (katalog klienta → strona producenta → rejestr KRS; po 6 naraz, ~1–2 min; zapis dopiero po „Przyjmij" — po przyjęciu kliknij ponownie dla kolejnych).</span>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ─── Dostęp klienta (T29) — konta użytkowników przypięte do klienta ──────────

function ClientAccessCard({ tenantId, tenantName }) {
  const [accounts, setAccounts] = useState(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy] = useState(null);
  const [issued, setIssued] = useState(null); // { email, password } — hasło pokazane RAZ
  const call = useCallback(async (payload) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(CLIENT_USERS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ tenant_id: tenantId, redirect_to: window.location.origin, ...payload }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
    return d;
  }, [tenantId]);
  // T2.3: lista przez edge (agencja nie czyta profili klienta wprost — RLS).
  const load = useCallback(async () => {
    try { const d = await call({ action: 'list' }); setAccounts(d.accounts || []); }
    catch (e) { setAccounts([]); toast.error(`Dostęp klienta: ${e.message}`); }
  }, [call]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e?.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em) return;
    setBusy('create');
    try {
      const d = await call({ action: 'create', email: em, full_name: fullName.trim() || undefined });
      if (d.mode === 'created' && d.mailed) toast.success(`Zaproszenie wysłane na ${d.email} — klient ustawi hasło z linku w mailu.`);
      else if (d.mode === 'created') { setIssued({ email: d.email, password: d.password }); toast.success(`Konto ${d.email} założone — mail nie wyszedł, przekaż hasło klientowi (widać je tylko teraz).`); }
      else toast.success(`${d.email} istniało — przypięte do „${tenantName}"`);
      setEmail(''); setFullName(''); load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };
  const resetPassword = async (a) => {
    if (!window.confirm(`Wysłać ${a.email} nowy link do ustawienia hasła?`)) return;
    setBusy(a.id);
    try {
      const d = await call({ action: 'reset_password', user_id: a.id });
      if (d.password) { setIssued({ email: d.email, password: d.password }); toast.success(d.mailed ? 'Link wysłany; hasło awaryjne poniżej (admin).' : 'Mail nie wyszedł — nowe hasło poniżej, przekaż klientowi.'); }
      else toast.success(`Link do ustawienia hasła wysłany na ${d.email}.`);
    }
    catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };
  const detach = async (a) => {
    if (!window.confirm(`Odpiąć ${a.email} od klienta „${tenantName}"? Konto zostaje, traci dostęp do danych klienta.`)) return;
    try { await call({ action: 'detach', user_id: a.id }); toast.success('Odpięto'); load(); }
    catch (err) { toast.error(err.message); }
  };
  const remove = async (a) => {
    if (!window.confirm(`USUNĄĆ konto ${a.email}? Klient nie zaloguje się już wcale. Tego nie da się cofnąć.`)) return;
    setBusy(a.id);
    try { await call({ action: 'delete', user_id: a.id }); toast.success('Konto usunięte'); load(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success('Skopiowane'); } catch (_) { toast.error('Nie udało się skopiować — zaznacz i skopiuj ręcznie.'); } };

  return (
    <Card>
      <CardHeader><CardTitle>Dostęp klienta</CardTitle><CardDescription>Podgląd dla klienta: po zalogowaniu widzi tylko swój katalog — zlecenia, Gotowość i to, co czeka na jego dane (np. GPSR). Bez kredytów i zespołu. Zaproszenie idzie mailem.</CardDescription></CardHeader>
      <CardBody className="grid gap-3">
        {issued && (
          <div className="rounded-lg border border-success/30 bg-success-light dark:bg-success/10 px-4 py-3 text-sm">
            <div className="font-semibold text-ink-900 dark:text-ink-50">Dane logowania dla klienta — hasło widać tylko teraz</div>
            <div className="mt-2 grid gap-1.5 font-mono text-[13px]">
              <div className="flex items-center gap-2"><span className="text-ink-500 w-16">login</span><span className="text-ink-900 dark:text-ink-50">{issued.email}</span><Button size="xs" variant="ghost" onClick={() => copy(issued.email)}>kopiuj</Button></div>
              <div className="flex items-center gap-2"><span className="text-ink-500 w-16">hasło</span><span className="text-ink-900 dark:text-ink-50">{issued.password}</span><Button size="xs" variant="ghost" onClick={() => copy(issued.password)}>kopiuj</Button></div>
              <div className="flex items-center gap-2"><span className="text-ink-500 w-16">adres</span><span className="text-ink-900 dark:text-ink-50 truncate">{window.location.origin}</span><Button size="xs" variant="ghost" onClick={() => copy(`Logowanie do MAMS:\n${window.location.origin}\nlogin: ${issued.email}\nhasło: ${issued.password}\nPo zalogowaniu możesz zmienić hasło przez „Nie pamiętam hasła".`)}>kopiuj komplet</Button></div>
            </div>
            <div className="mt-2 flex gap-2"><Button size="xs" variant="ghost" onClick={() => setIssued(null)}>Ukryj</Button></div>
          </div>
        )}
        {accounts == null ? <SkeletonRow /> : accounts.length === 0 ? (
          <p className="text-sm text-ink-500 dark:text-ink-400">Nikt jeszcze nie ma dostępu.</p>
        ) : (
          <ul className="divide-y divide-ink-100 dark:divide-ink-800 rounded-lg border border-ink-200 dark:border-ink-700">
            {accounts.map(a => (
              <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="min-w-0"><span className="block font-medium text-ink-900 dark:text-ink-50 truncate">{a.email}</span>{a.full_name && <span className="block text-2xs text-ink-500">{a.full_name}</span>}</span>
                <span className="flex items-center gap-1">
                  <Button size="xs" variant="ghost" loading={busy === a.id} onClick={() => resetPassword(a)}>Wyślij link ponownie</Button>
                  <Button size="xs" variant="ghost" onClick={() => detach(a)}>Odepnij</Button>
                  <Button size="xs" variant="ghost" onClick={() => remove(a)} title="Usuń konto"><Trash2 className="w-4 h-4 text-ink-400 hover:text-danger" /></Button>
                </span>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={add} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="e-mail klienta" autoComplete="off" />
          <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="imię i nazwisko (opcjonalnie)" autoComplete="off" />
          <Button type="submit" loading={busy === 'create'} disabled={!email.trim()}>Zaproś klienta</Button>
        </form>
        <FieldHint>Nowy e-mail = konto + mail z linkiem „ustaw hasło” (link ważny 24 h). Gdy mail nie wyjdzie, pokażemy hasło raz. Jedno konto = jeden klient; konta zespołu agencji nie da się tu przypiąć.</FieldHint>
      </CardBody>
    </Card>
  );
}

// ─── Dane klienta (hook) ────────────────────────────────────────────────────

function useTenant(tenantId) {
  const [data, setData] = useState({ tenant: null, connections: [], channels: [], priceRule: null, loading: true });
  const reload = useCallback(async () => {
    invalidate('clients:'); // PERF-UX: lista klientów odświeży się przy powrocie
    if (!tenantId) return;
    const [t, c, ch, pr] = await Promise.all([
      supabase.from('tenants').select('*').eq('id', tenantId).single(),
      supabase.from('connections').select('id, tenant_id, system, label, status, tested_at, last_error, meta, created_at').eq('tenant_id', tenantId).order('created_at'),
      supabase.from('channels').select('*').eq('tenant_id', tenantId).order('created_at'),
      supabase.from('price_rules').select('*').eq('tenant_id', tenantId).is('channel_id', null).maybeSingle(),
    ]);
    setData({ tenant: t.data, connections: c.data || [], channels: ch.data || [], priceRule: pr.data || null, loading: false });
  }, [tenantId]);
  useEffect(() => { reload(); }, [reload]);
  return { ...data, reload };
}

// ─── Kreator ────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Klient' },
  { id: 2, label: 'Połączenia i kanały' },
  { id: 3, label: 'Reguły' },
];

function Stepper({ step }) {
  return (
    <ol className="flex items-center gap-2 mb-6">
      {STEPS.map((s, i) => {
        const done = step > s.id, active = step === s.id;
        return (
          <li key={s.id} className="flex items-center gap-2">
            <span className={cn(
              'w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold',
              done ? 'bg-success text-white' : active ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400',
            )}>{done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : s.id}</span>
            <span className={cn('text-sm font-medium', active ? 'text-ink-900 dark:text-ink-50' : 'text-ink-500 dark:text-ink-400')}>{s.label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-ink-300 dark:text-ink-600 mx-1" />}
          </li>
        );
      })}
    </ol>
  );
}

// T2.1: klient „bez połączeń (katalog z pliku)” też potrzebuje kanałów — bez nich zlecenia nie da się złożyć na klienta,
// a Gotowość zostaje pusta. Kanał bez połączenia = treść w języku rynku + plik; publikacja po podpięciu konta klienta.
const FILE_ONLY_CHANNELS = [
  ...MARKETS.filter(m => ['de', 'at', 'cz', 'sk', 'pl'].includes(m.id)).map(m => ({ platform: 'kaufland', market: m.id, label: `Kaufland · ${m.label}` })),
  { platform: 'allegro', market: 'pl', label: 'Allegro · Polska' },
  { platform: 'amazon', market: 'de', label: 'Amazon · Niemcy' },
];
function FileOnlyChannels({ tenantId, channels, onChange }) {
  const [busy, setBusy] = useState(null);
  const find = (c) => channels.find(x => x.platform === c.platform && x.market === c.market);
  const toggle = async (c) => {
    const ex = find(c);
    setBusy(`${c.platform}:${c.market}`);
    const { error } = ex
      ? await supabase.from('channels').update({ enabled: !ex.enabled }).eq('id', ex.id)
      : await supabase.from('channels').insert({ tenant_id: tenantId, connection_id: null, platform: c.platform, market: c.market, language: marketLanguage(c.market), enabled: true, settings: {} });
    setBusy(null);
    if (error) return toast.error(error.message);
    onChange?.();
  };
  return (
    <div className="grid gap-2">
      <div className="text-sm text-ink-600 dark:text-ink-300">Bez połączenia: zaznacz, na które kanały robimy treść. Produkty przyjdą z pliku, a publikacja ruszy, gdy podepniesz konto klienta.</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {FILE_ONLY_CHANNELS.map(c => {
          const on = !!find(c)?.enabled;
          const k = `${c.platform}:${c.market}`;
          return (
            <button key={k} type="button" disabled={busy === k} onClick={() => toggle(c)}
              className={cn('flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors',
                on ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-ink-900 dark:text-ink-50' : 'border-ink-200 dark:border-ink-700 text-ink-700 dark:text-ink-300 hover:border-ink-300')}>
              <span>{c.label} <span className="text-2xs text-ink-500">· treść: {marketLanguage(c.market).toUpperCase()}</span></span>
              {on && <Check className="w-4 h-4 text-brand-600" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Wizard({ user, orgId = null, welcome = false, onDone, onCancel }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [mode, setMode] = useState('direct');
  const [nameError, setNameError] = useState('');
  const [tenantId, setTenantId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addingSystem, setAddingSystem] = useState(null);
  const [rulesDirty, setRulesDirty] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const rulesSave = useRef(null);
  const { tenant, connections, channels, priceRule, reload } = useTenant(tenantId);
  const finish = async () => {
    if (rulesDirty && rulesSave.current) {
      setFinishing(true);
      const ok = await rulesSave.current();
      setFinishing(false);
      if (!ok) return;
    }
    onDone(tenantId);
  };

  const createTenant = async () => {
    const n = name.trim();
    if (n.length < 2) return setNameError('Podaj nazwę klienta.');
    setBusy(true);
    // v9: id z przeglądarki i insert bez RETURNING — członek organizacji (nie-admin) nie przechodzi polityki SELECT
    // na wierszu wstawianym w tej samej instrukcji (can_access_tenant nie widzi jeszcze nowego klienta) → 42501.
    const id = crypto.randomUUID();
    const { error } = await supabase.from('tenants').insert({ id, name: n, mode, created_by: user?.id ?? null, ...(orgId ? { org_id: orgId } : {}) });
    invalidate('clients:');
    setBusy(false);
    // Plan v12/4.1: wyzwalacz tenants_plan_limit odrzuca klienta ponad limit planu komunikatem „plan_limit: …” — pokazujemy go bez prefiksu.
    if (error) return setNameError(error.code === '23505' ? 'Klient o tej nazwie już istnieje.' : String(error.message || '').replace(/^.*plan_limit:\s*/, ''));
    setTenantId(id);
    setStep(2);
  };

  return (
    <div className="max-w-4xl">
      <PageHeader
        title={welcome ? 'Witaj w MAMS — dodaj pierwszego klienta' : 'Nowy klient'}
        subtitle={welcome
          ? 'Konto agencji gotowe: 20 kredytów na start (1 kredyt = 1 produkt na 1 kanale). Trzy kroki: kim jest klient, przez co sprzedaje, jakie ma reguły.'
          : 'Trzy kroki: kim jest klient, przez co sprzedaje, jakie ma reguły. Klucze API trafiają do sejfu, nie do bazy.'}
        action={<Button variant="secondary" onClick={onCancel} leftIcon={<ArrowLeft className="w-4 h-4" />}>Wszyscy klienci</Button>}
      />
      <Stepper step={step} />

      {step === 1 && (
        <Card>
          <CardHeader><CardTitle>1. Jak działamy z tym klientem?</CardTitle><CardDescription>Tryb podpowiada systemy w kroku 2 — potem można dodać dowolne.</CardDescription></CardHeader>
          <CardBody className="grid gap-5">
            <div>
              <Label required>Nazwa klienta</Label>
              <Input value={name} onChange={e => { setName(e.target.value); setNameError(''); }} placeholder="np. Sklep Elektro Kowalski" error={!!nameError} autoFocus />
              <FieldError>{nameError}</FieldError>
            </div>
            <ModeSwitch value={mode} onChange={setMode} />
          </CardBody>
          <CardFooter className="flex justify-end">
            <Button onClick={createTenant} loading={busy} rightIcon={<ArrowRight className="w-4 h-4" />}>Dalej: połączenia</Button>
          </CardFooter>
        </Card>
      )}

      {step === 2 && tenantId && (
        <div className="grid gap-5">
          <Card>
            <CardHeader><CardTitle>2. Połączenia</CardTitle><CardDescription>Wybierz system, wklej klucze i kliknij „Zapisz i testuj". Z udanego testu wynikają kanały poniżej.</CardDescription></CardHeader>
            <CardBody className="grid gap-4">
              {connections.map(c => (
                <ConnectionCard key={c.id} tenantId={tenantId} system={c.system} connection={c} onSaved={reload} onRemoved={reload} />
              ))}
              {addingSystem && (
                <ConnectionCard tenantId={tenantId} system={addingSystem} connection={null} onSaved={() => { setAddingSystem(null); reload(); }} onRemoved={() => setAddingSystem(null)} />
              )}
              {!addingSystem && (
                <div>
                  {connections.length > 0 && <div className="text-sm font-medium text-ink-700 dark:text-ink-300 mb-2">Dodaj kolejne połączenie</div>}
                  <SystemPicker existing={connections.map(c => c.system)} preferred={mode} onPick={setAddingSystem} />
                </div>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader><CardTitle>Kanały</CardTitle><CardDescription>Na które kanały robimy oferty. Język treści wynika z rynku.</CardDescription></CardHeader>
            <CardBody>
              {connections.some(c => c.status === 'ok')
                ? <ChannelsEditor tenantId={tenantId} connections={connections.filter(c => c.status === 'ok')} channels={channels} onChange={reload} autoDefault />
                : <FileOnlyChannels tenantId={tenantId} channels={channels} onChange={reload} />}
            </CardBody>
            <CardFooter className="flex justify-between items-center">
              <span className="text-xs text-ink-500 dark:text-ink-400">{channels.filter(c => c.enabled).length ? `Kanały: ${channels.filter(c => c.enabled).length}` : 'Zaznacz co najmniej jeden kanał'}</span>
              <Button onClick={() => setStep(3)} disabled={!channels.some(c => c.enabled)} rightIcon={<ArrowRight className="w-4 h-4" />}>Dalej: reguły</Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {step === 3 && tenant && (
        <Card>
          <CardHeader><CardTitle>3. Reguły klienta</CardTitle><CardDescription>Cena na kanałach, numery WEEE/BattG. „Zakończ" zapisuje to, co tu ustawisz.</CardDescription></CardHeader>
          <CardBody><RulesForm tenant={tenant} priceRule={priceRule} onSaved={reload} onDirtyChange={setRulesDirty} saveRef={rulesSave} /></CardBody>
          <CardFooter className="flex justify-between items-center">
            <Button variant="ghost" onClick={() => setStep(2)} leftIcon={<ArrowLeft className="w-4 h-4" />}>Wstecz</Button>
            <div className="flex items-center gap-3">
              <Button variant="success" onClick={finish} loading={finishing} leftIcon={<Check className="w-4 h-4" />}>{rulesDirty ? 'Zapisz i zakończ' : 'Zakończ'}</Button>
            </div>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}

// ─── Szczegóły / edycja ─────────────────────────────────────────────────────

function TenantDetail({ tenantId, onBack, clientMode = false }) {
  const { tenant, connections, channels, priceRule, loading, reload } = useTenant(tenantId);
  const [addingSystem, setAddingSystem] = useState(null);
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => { if (tenant) setName(tenant.name); }, [tenant]);

  const saveName = async () => {
    const n = name.trim();
    if (n.length < 2 || n === tenant.name) return;
    setSavingName(true);
    const { error } = await supabase.from('tenants').update({ name: n }).eq('id', tenantId);
    invalidate('clients:');
    setSavingName(false);
    if (error) return toast.error(error.code === '23505' ? 'Klient o tej nazwie już istnieje.' : error.message);
    toast.success('Nazwa zapisana'); reload();
  };
  const setMode = async (mode) => {
    if (mode === tenant.mode) return;
    const { error } = await supabase.from('tenants').update({ mode }).eq('id', tenantId);
    invalidate('clients:');
    if (error) return toast.error(error.message);
    reload();
  };
  const removeTenant = async () => {
    if (!window.confirm(`Usunąć klienta „${tenant.name}" razem z połączeniami i kanałami? Zlecenia zostają (bez klienta).`)) return;
    const { error } = await supabase.from('tenants').delete().eq('id', tenantId);
    invalidate('clients:');
    if (error) return toast.error(error.message);
    toast.success('Klient usunięty');
    onBack();
  };

  if (loading) return <div className="grid gap-3"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>;
  if (!tenant) return <EmptyState icon={<Users className="w-7 h-7" />} title="Nie ma takiego klienta" description="Klient mógł zostać usunięty albo nie masz do niego dostępu." action={onBack ? <Button onClick={onBack}>Wróć do listy</Button> : null} />;

  const okConns = connections.filter(c => c.status === 'ok');
  const liveChannels = channels.filter(c => c.enabled);

  return (
    <div>
      <PageHeader
        title={tenant.name}
        badge={<ModeBadge mode={tenant.mode} />}
        subtitle={
          <span className="inline-flex flex-wrap gap-x-4 gap-y-1">
            <span>{connections.length === 0 ? 'Bez połączeń' : `${okConns.length}/${connections.length} połączeń działa`}</span>
            <span>{liveChannels.length === 0 ? 'Bez kanałów' : `${liveChannels.length} ${liveChannels.length === 1 ? 'kanał' : liveChannels.length < 5 ? 'kanały' : 'kanałów'}`}</span>
            <span>dodany {formatDateTime(tenant.created_at)}</span>
          </span>
        }
        action={clientMode ? null : (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onBack} leftIcon={<ArrowLeft className="w-4 h-4" />}>Wszyscy klienci</Button>
            <Button variant="ghost" onClick={removeTenant} leftIcon={<Trash2 className="w-4 h-4" />} className="text-danger hover:text-danger-dark hover:bg-danger-light/50">Usuń klienta</Button>
          </div>
        )}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="grid gap-5 content-start">
          <Card>
            <CardHeader><CardTitle>Połączenia</CardTitle><CardDescription>Status z ostatniego testu. Klucze zmienisz przyciskiem „Zmień klucze" — nigdy nie są pokazywane.</CardDescription></CardHeader>
            <CardBody className="grid gap-4">
              {connections.map(c => <ConnectionCard key={c.id} tenantId={tenantId} system={c.system} connection={c} onSaved={reload} onRemoved={reload} />)}
              {addingSystem && <ConnectionCard tenantId={tenantId} system={addingSystem} connection={null} onSaved={() => { setAddingSystem(null); reload(); }} onRemoved={() => setAddingSystem(null)} />}
              {!addingSystem && (
                <div>
                  <div className="text-sm font-medium text-ink-700 dark:text-ink-300 mb-2">{connections.length ? 'Dodaj kolejne połączenie' : 'Dodaj połączenie'}</div>
                  <SystemPicker existing={connections.map(c => c.system)} preferred={tenant.mode} onPick={setAddingSystem} />
                  {connections.length === 0 && <p className="text-xs text-ink-500 dark:text-ink-400 mt-3">Bez połączenia klient też działa: katalog z pliku, publikacja bez konta klienta.</p>}
                </div>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader><CardTitle>Kanały</CardTitle><CardDescription>Zaznaczone kanały dostają oferty. Zlecenie wybiera spośród włączonych.</CardDescription></CardHeader>
            <CardBody className="grid gap-4">
              {/* T2.1 (audyt): klient bez połączenia też może mieć kanały treści (katalog z pliku) — jak w kreatorze. */}
              {okConns.length === 0 && !clientMode && <FileOnlyChannels tenantId={tenantId} channels={channels} onChange={reload} />}
              <ChannelsEditor tenantId={tenantId} connections={okConns} channels={channels} onChange={reload} />
            </CardBody>
          </Card>
          <ManufacturersCard tenantId={tenantId} />
        </div>

        <div className="grid gap-5 content-start">
          <Card>
            <CardHeader><CardTitle>Klient</CardTitle></CardHeader>
            <CardBody className="grid gap-5">
              <div>
                <Label>Nazwa</Label>
                <div className="flex gap-2">
                  <Input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveName()} className="flex-1" />
                  {name.trim() !== tenant.name && <Button size="md" onClick={saveName} loading={savingName}>Zapisz</Button>}
                </div>
              </div>
              {!clientMode && (
                <div>
                  <Label>Jak działamy</Label>
                  <ModeSwitch value={tenant.mode} onChange={setMode} compact />
                </div>
              )}
            </CardBody>
          </Card>
          <RulesCard tenant={tenant} priceRule={priceRule} onSaved={reload} />
          {/* STYLE-C (plan v13 T7): profil marki klienta → styl opisów. Konto klienta widzi, agencja/admin edytuje. */}
          <BrandProfileCard tenant={tenant} onSaved={reload} readOnly={clientMode} />
          {!clientMode && <ClientAccessCard tenantId={tenantId} tenantName={tenant.name} />}
          {!clientMode && <p className="text-2xs text-ink-400 dark:text-ink-500 px-1">Identyfikator klienta (dla silnika): <code className="select-all">{tenant.id}</code></p>}
        </div>
      </div>
    </div>
  );
}

// ─── Lista ──────────────────────────────────────────────────────────────────

function TenantList({ userId, onOpen, onNew }) {
  const [rows, setRows] = useState(null);
  // Plan v8/E6: „Moi klienci” — pracownik widzi wszystkich klientów organizacji, ale może zawęzić do tych, których obsługuje.
  const [mine, setMine] = useState(() => { try { return localStorage.getItem('mams.clients.mine') === '1'; } catch { return false; } });
  const [assigned, setAssigned] = useState(new Set());
  const loadAssigned = useCallback(async () => {
    if (!userId) return;
    const { data, error } = await supabase.from('tenant_assignees').select('tenant_id').eq('user_id', userId);
    if (!error) setAssigned(new Set((data || []).map(r => r.tenant_id)));
  }, [userId]);
  useEffect(() => { loadAssigned(); }, [loadAssigned]);
  const toggleAssigned = async (e, tenantId) => {
    e.stopPropagation();
    const on = assigned.has(tenantId);
    const { error } = on
      ? await supabase.from('tenant_assignees').delete().eq('tenant_id', tenantId).eq('user_id', userId)
      : await supabase.from('tenant_assignees').insert({ tenant_id: tenantId, user_id: userId });
    if (error) { toast.error(error.message); return; }
    loadAssigned();
  };
  const setMineSaved = (v) => { setMine(v); try { localStorage.setItem('mams.clients.mine', v ? '1' : '0'); } catch { /* bez pamięci */ } };
  // PERF-UX (21.10): lista klientów od razu z cache (ostatni stan), świeże dane w tle; zapisy robią `invalidate('clients:')`.
  const load = useCallback(async () => {
    try {
      await loadWithCache('clients:list', async () => {
        const [{ data: tenants, error }, { data: conns }, { data: chans }] = await Promise.all([
          supabase.from('tenants').select('id, name, mode, org_id, weee_number, notes, created_at').order('created_at', { ascending: false }),
          supabase.from('connections').select('id, tenant_id, system, status'),
          supabase.from('channels').select('id, tenant_id, platform, market, enabled'),
        ]);
        if (error) throw error;
        return (tenants || []).map(t => ({
          ...t,
          connections: (conns || []).filter(c => c.tenant_id === t.id),
          channels: (chans || []).filter(c => c.tenant_id === t.id && c.enabled),
        }));
      }, (rows) => setRows(rows));
    } catch (e) { toast.error(e.message); setRows(r => r || []); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader
        title="Klienci"
        subtitle="Każdy klient ma swój katalog, konta na platformach i kanały, na które robimy oferty."
        action={
          <div className="flex gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-ink-600 dark:text-ink-300 mr-2 cursor-pointer select-none">
              <input type="checkbox" checked={mine} onChange={(e) => setMineSaved(e.target.checked)} className="w-4 h-4 accent-brand-600" />
              Tylko moi klienci
            </label>
            <Button variant="ghost" onClick={load} leftIcon={<RefreshCw className="w-4 h-4" />}>Odśwież</Button>
            <Button onClick={onNew} leftIcon={<Plus className="w-4 h-4" />}>Dodaj klienta</Button>
          </div>
        }
      />
      {rows == null ? (
        <div className="grid gap-3"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<Users className="w-7 h-7" />} title="Jeszcze nie ma klientów" description="Dodaj klienta, podepnij jego konto (BaseLinker, Sellasist, Kaufland, Allegro) i zaznacz kanały. Zlecenia będą wybierać klienta zamiast platformy." action={<Button onClick={onNew} leftIcon={<Plus className="w-4 h-4" />}>Dodaj klienta</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/50 text-2xs uppercase tracking-wide text-ink-500 dark:text-ink-400">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Klient</th>
                <th className="text-left px-5 py-3 font-bold">Połączenia</th>
                <th className="text-left px-5 py-3 font-bold">Kanały</th>
                <th className="text-left px-5 py-3 font-bold">Utworzony</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
              {rows.filter(t => !mine || assigned.has(t.id)).map(t => (
                <tr key={t.id} onClick={() => onOpen(t.id)} className="cursor-pointer hover:bg-ink-50/70 dark:hover:bg-ink-800/40 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white inline-flex items-center justify-center"><Building2 className="w-4 h-4" /></span>
                      <div>
                        <div className="font-semibold text-ink-900 dark:text-ink-50">{t.name}</div>
                        <div className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">{t.mode === 'integrator' ? 'przez integratora' : 'bezpośrednio na platformie'}{t.weee_number ? ` · WEEE ${t.weee_number}` : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {t.connections.length === 0 && <span className="text-ink-400 text-xs">brak — katalog z pliku</span>}
                      {t.connections.map(c => (
                        <span key={c.id} className="inline-flex items-center gap-1.5 text-xs">
                          <SystemMark system={c.system} size="sm" />
                          <span className="font-medium text-ink-800 dark:text-ink-200">{SYSTEMS[c.system]?.label}</span>
                          <span className={cn('w-1.5 h-1.5 rounded-full', c.status === 'ok' ? 'bg-success' : c.status === 'error' ? 'bg-danger' : 'bg-ink-300')} title={c.status === 'ok' ? 'działa' : c.status === 'error' ? 'błąd' : 'nietestowane'} />
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {t.channels.length === 0 && <span className="text-ink-400 text-xs">—</span>}
                      {t.channels.map(c => <Badge key={c.id} variant="brand" size="sm">{SYSTEMS[c.platform.replace('_channel', '') === 'base' ? 'baselinker' : c.platform.replace('_channel', '')]?.label ?? c.platform} {c.market.toUpperCase()}</Badge>)}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-ink-500 dark:text-ink-400 text-xs">{formatDateTime(t.created_at)}</td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <button type="button" onClick={(e) => toggleAssigned(e, t.id)} title={assigned.has(t.id) ? 'Obsługuję — kliknij, żeby odpiąć' : 'Oznacz jako mój klient'} className={cn('mr-3 text-2xs font-semibold px-2 py-1 rounded border transition-colors', assigned.has(t.id) ? 'border-brand-300 bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200 dark:border-brand-700' : 'border-ink-200 text-ink-500 hover:border-brand-300 hover:text-brand-700 dark:border-ink-700 dark:text-ink-400')}>
                      {assigned.has(t.id) ? 'Mój' : '+ Mój'}
                    </button>
                    <ChevronRight className="w-4 h-4 text-ink-300 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─── Strona ─────────────────────────────────────────────────────────────────

export default function ClientsPage({ user, fixedTenantId = null, orgId = null, startWizard = false, onWizardStarted = null, onNewOrder = null }) {
  // v9/A3: nowa agencja po rejestracji trafia od razu do kreatora „pierwszy klient”.
  // 21.10: otwarty klient w adresie (&client=<id>) — F5 zostaje na tym kliencie.
  const [view, setView] = useState(() => {
    if (startWizard) return { kind: 'wizard', welcome: true };
    const id = !fixedTenantId ? getUrlParam('client') : null;
    return id ? { kind: 'detail', id } : { kind: 'list' };
  });
  useEffect(() => { if (startWizard && onWizardStarted) onWizardStarted(); }, [startWizard, onWizardStarted]);
  useEffect(() => { if (!fixedTenantId) setUrlParams({ client: view.kind === 'detail' ? view.id : null }); }, [view, fixedTenantId]);
  // T29: konto klienta — tylko szczegóły własnego klienta, bez listy, bez usuwania.
  if (fixedTenantId) return <TenantDetail tenantId={fixedTenantId} onBack={null} clientMode />;
  if (view.kind === 'wizard') return <Wizard user={user} orgId={orgId} welcome={!!view.welcome} onDone={(id) => {
    // T2.1: po kreatorze prosto do pierwszego zlecenia tego klienta (klient i kanały już wybrane).
    if (onNewOrder) { toast.success('Klient zapisany — wgraj plik z produktami.'); onNewOrder(id); }
    else setView({ kind: 'detail', id });
  }} onCancel={() => setView({ kind: 'list' })} />;
  if (view.kind === 'detail') return <TenantDetail tenantId={view.id} onBack={() => setView({ kind: 'list' })} />;
  return <TenantList userId={user?.id} onOpen={(id) => setView({ kind: 'detail', id })} onNew={() => setView({ kind: 'wizard' })} />;
}
